<script>
  // Oeffentliche Team-Umfrage zum Framework (ohne Anmeldung, Link: /app/team-survey?id=<Umfrage>).
  // Stufe collect: eigene Aufgaben eintragen. Stufe select: die zusammengefasste Liste bewerten.
  // Die Antworten tragen nur einen Zufallsschluessel dieses Browsers - anonym, aber wiederaufnehmbar.
  // Mit persoenlichem Link (&invite=) liegt der Entwurf bis zum Absenden zusaetzlich auf dem Server (Geraetewechsel).
  import { onDestroy, onMount, tick } from "svelte";
  import { Loading } from "carbon-components-svelte";
  import Button from "lib/components/Button.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  // Oeffentliche Seite (Rueckmeldung 04.10.): keine roten Plattform-Meldungen wie "survey not found" - jeder Fehler wird
  // hier selbst freundlich behandelt (Hinweis auf der Seite, feste Beispiele, Liste ohne Themen)
  const request = (url, options = {}) => fetchWithSession(url, { ...options, suppressErrorToast: true, suppressSuccessToast: true });
  import { activityFitQuestions } from "lib/util/frameworkReadiness.js";
  import SideNavHeaderLogo from "lib/components/layout/sideNav/SideNavHeaderLogo.svelte";
  import { pageTitleStore } from "@stores/pageTitleStore.js";
  import EditIcon from "../settings/svg/EditIcon.svelte";
  import { DURATIONS, IDEA_AREAS, ideaExamples, taskExample, teamSurveyTexts, weightOf } from "./teamSurveyTexts.js";

  const surveyId = new URLSearchParams(window.location.search).get("id") || "";
  // Vorschau aus dem Panel: alles bedienbar, aber nichts wird gesendet (wie die Vorschau im Potential Scout)
  const preview = new URLSearchParams(window.location.search).has("preview");
  const invite = new URLSearchParams(window.location.search).get("invite") || "";
  const DRAFT_DEBOUNCE_MS = 1500;
  // Hell/Dunkel (Rueckmeldung 04.10.): jeder Besuch startet hell; dunkel gilt nur, solange die Seite offen ist
  let dark = false;

  function toggleTheme() {
    dark = !dark;
  }
  // je persoenlicher Einladung eigener Schluessel - zwei Eingeladene am selben Rechner ueberschreiben sich sonst
  const storagePrefix = `paxray.team-survey.${surveyId}${invite ? `.${invite}` : ""}`;
  const participant = participantKey();
  const options = Object.fromEntries(activityFitQuestions.map((question) => [question.id, question.options]));
  const UNKNOWN = "?";
  const POLL_MS = 5000;

  let survey = null;
  let loading = true;
  let error = "";
  let notice = "";
  let view = "intro"; // intro | main | sent
  let saving = false;
  let pollTimer = null;
  let draftTimer = null;
  let lastSnapshot = "";
  let draftState = ""; // "" | saved
  let sentElsewhere = false;

  // Stufe collect
  let tasks = [];
  let form = blankForm();
  let editIndex = -1;
  let nameInput;
  // Begruessung (Rueckmeldung Alex 02.10.): Bereich und Zustaendigkeit fuer alle, dann freiwillig Aufgabenideen von Pax AI.
  // Beides bleibt im Browser - der Server bekommt es nur fuer die Ideen, gespeichert wird es dort nicht.
  let assist = blankAssist();
  let assistLoading = false;
  let assistNote = "";
  let ideasOpen = true;
  let ideaKind = "";
  let moreLoading = false;
  // Pruefseite: die eigene Liste nach Themen (wie Umfrage 2), Name -> Thema; neu geordnet nur bei neuen Namen
  let grouping = { names: {}, themes: [] };
  let areaGrouping = {}; // je Bereich die Themen von Pax AI (Umfrage mit Bereichen)
  let groupingLoading = false;
  let formStep = 1; // je Aufgabe erst Name und Ablauf, dann die Kurzfragen (Rueckmeldung 02.10.: alles auf einmal erschlug)
  let descAsked = false; // einmal nachfragen, wenn der Ablauf fehlt - die Ableitung der L4 braucht ihn
  let nameLeft = false; // Hinweis zum Namen erst nach dem Verlassen des Feldes, nicht schon beim ersten Wort
  // Gibt die Umfrage Prozesse (L1) vor, wird je Prozess nacheinander gefragt, zum Schluss nach sonstigen Aufgaben
  let processIndex = 0;

  // Stufe select
  let votes = {};
  let openComments = {};
  let missing = []; // in Stufe select ergaenzte Aufgaben, mit denselben Angaben wie in Stufe collect
  let addingMissing = false;
  let filter = "all";

  // Seite in der Sprache des Browsers (folgt meist dem Betriebssystem), sonst die der Umfrage (Rueckmeldung 04.10.);
  // die Einladungs-Mails gehen vorher raus und bleiben in der eingestellten Sprache
  // ?lang=en erzwingt eine Sprache (z. B. fuer Screenshots), sonst Browsersprache
  const browserLanguage = [new URLSearchParams(window.location.search).get("lang"), ...(navigator.languages || [navigator.language])].map((code) => String(code || "").slice(0, 2)).find((code) => teamSurveyTexts[code]);
  $: language = browserLanguage || survey?.language || "de";
  $: t = teamSurveyTexts[language] || teamSurveyTexts.de;
  $: stepTotal = survey?.mode === "both" ? 2 : 1;
  // Schritte innerhalb der laufenden Umfrage, angezeigt wie im Framework Assistant
  $: progressSteps = survey?.stage === "select" ? t.steps.select : t.steps.collect;
  $: progressStep = survey?.stage === "select" ? (view === "intro" ? 0 : 1) : ({ intro: 0, assist: 1, main: 2, review: 3 }[view] ?? 3);
  $: stepNumber = survey?.stage === "collect" || survey?.mode === "select" ? 1 : stepTotal;
  $: deadline = survey ? formatDate(survey.stage === "collect" ? survey.collect_until : survey.select_until) : "";
  $: late = survey && new Date(survey.now) > new Date(survey.stage === "collect" ? survey.collect_until : survey.select_until);
  $: themes = survey?.themes || [];
  $: allTasks = themes.flatMap((theme) => theme.tasks);
  $: examples = ideaExamples(language, survey?.team);
  // Ideen von Pax AI, sonst (abgelehnt oder nicht erreichbar) die festen Beispiele des Teams
  $: aiIdeas = Boolean(assist.ideas && IDEA_AREAS.some((area) => assist.ideas[area]?.length));
  // Oben die Taetigkeiten, darunter nur die Vorschlaege der gewaehlten (Rueckmeldung 02.10.: hoechstens zwei Reihen)
  $: ideaKinds = IDEA_AREAS.filter((area) => ((aiIdeas ? assist.ideas[area] : examples[area]) || []).length);
  $: if (!ideaKinds.includes(ideaKind)) ideaKind = ideaKinds[0] || "";
  $: ideaNames = (aiIdeas ? assist.ideas[ideaKind] : examples[ideaKind]) || [];
  $: taken = new Set(tasks.map((task) => task.name.toLocaleLowerCase()));
  $: model = taskExample(language, survey?.team);
  // Aufnahmezeitraum aus den Einstellungen: nur dann fragt die Pruefseite je Aufgabe "In diesem Zeitraum?"
  $: period = survey?.period_from && survey?.period_until ? [formatDay(survey.period_from), formatDay(survey.period_until)] : null;
  // Pruefseite: ohne Bereiche eine Liste nach Themen; mit Bereichen je Bereich ("L1: L2") die Themen von Pax AI darin
  // als L3 (Rueckmeldung 07.10.)
  $: reviewSections = processSteps.length
    ? areaSections(tasks).map((section) => ({ ...section, themes: themesOf(section.items, areaGrouping[section.key] || NO_GROUPING) }))
    : [{ label: "", key: "", items: indexed(tasks), themes: themesOf(indexed(tasks), grouping) }];
  // Task Mining sucht Vorgaenge (Verb + Gegenstand) - Werkzeuge wie "E-Mails" und Einzelwoerter bekommen einen Hinweis
  $: nameWords = form.name.trim().split(/\s+/).filter(Boolean);
  $: channelWord = nameWords.length <= 2 ? nameWords.find((word) => t.collect.channelWords.test(word)) : "";
  $: coach = !nameLeft ? "" : channelWord ? t.collect.coachChannel(channelWord) : nameWords.length === 1 && form.name.trim().length > 3 ? t.collect.coachVerb : "";
  $: suggestions = (survey?.suggestions || []).filter(
    (name) => !tasks.some((task) => task.name.toLocaleLowerCase() === name.toLocaleLowerCase()),
  );
  // Pflicht: Name, Kurzbeschreibung und drei Einordnungen; Dauer und Gleichfoermigkeit bleiben freiwillig, weil sie
  // nach Leistungsbewertung klingen koennten (Rueckmeldung 07.10.)
  const REQUIRED_FIELDS = ["frequency", "digital", "continuity"];
  const OPTIONAL_FIELDS = ["duration", "standardization"];
  $: textMissing = (form.name.trim() ? 0 : 1) + (form.description.trim() ? 0 : 1);
  $: missingCount = textMissing + REQUIRED_FIELDS.filter((field) => !form[field]).length;
  $: formStarted = Boolean(form.name.trim() || form.description.trim() || form.share || form.frequency || form.duration || form.digital || form.continuity || form.standardization);
  $: changedCount = allTasks.filter((task) => votes[task.key] && votes[task.key].keep !== recommended(task)).length;
  $: filterCounts = allTasks.reduce((counts, task) => ({ ...counts, [task.status]: (counts[task.status] || 0) + 1 }), { all: allTasks.length });
  $: processes = survey?.processes || [];
  // name "" steht fuer "Sonstige Aufgaben" (Aufgaben ohne Prozess)
  // key trennt gleich benannte L2 unter verschiedenen L1; "" ist "Sonstige Aufgaben"
  $: processSteps = processes.length ? [...processes.map((process) => ({ id: process.id || null, name: process.name, parent: process.parent || "", key: process.id || `${process.parent || ""}|${process.name}` })), { id: null, name: "", parent: "", key: "" }] : [];
  $: areaCounts = Object.fromEntries(processSteps.map((process) => [process.key, tasks.filter((task) => processOf(task) === process.key).length]));
  $: currentProcess = processSteps[processIndex] || null;
  $: listedTasks = tasks.map((task, index) => ({ task, index })).filter(({ task }) => !currentProcess || processOf(task) === currentProcess.key);
  $: if (survey) persist({ tasks, form, votes, missing, assist, processIndex });

  function participantKey() {
    const fallback = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    try {
      let key = localStorage.getItem(`${storagePrefix}.participant`);
      if (!key) {
        key = window.crypto?.randomUUID?.() || fallback();
        localStorage.setItem(`${storagePrefix}.participant`, key);
      }
      return key;
    } catch {
      return fallback();
    }
  }

  // Jede Aenderung: sofort im Browser, kurz danach (mit persoenlichem Link) auch auf dem Server.
  // Der Vergleich mit lastSnapshot verhindert, dass schon das Laden als Aenderung zaehlt.
  function persist(state) {
    const snapshot = JSON.stringify(state);
    if (snapshot === lastSnapshot) return;
    lastSnapshot = snapshot;
    try {
      localStorage.setItem(`${storagePrefix}.draft`, JSON.stringify({ ...state, savedAt: Date.now() }));
    } catch {
      // Ohne Speicher geht nur der Zwischenstand verloren, nicht die Umfrage
    }
    draftState = "saved";
    clearTimeout(draftTimer);
    if (invite && !preview && !survey.invite?.done && view !== "sent") draftTimer = setTimeout(saveServerDraft, DRAFT_DEBOUNCE_MS);
  }

  async function saveServerDraft() {
    clearTimeout(draftTimer);
    draftTimer = null;
    if (saving || view === "sent" || survey.invite?.done) return; // schon abgeschickt: der Server lehnt den Stand ab (409)
    const data = survey.stage === "collect" ? { tasks, form, processIndex } : { votes, missing };
    try {
      await request(`/api/damprojects/team-survey/${surveyId}/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invite, stage: survey.stage, draft: data }),
        keepalive: true,
      });
    } catch {
      // Der Browser hat den Stand ohnehin; beim naechsten Tippen wird erneut gespeichert
    }
  }

  function flushDraft() {
    if (document.visibilityState === "hidden" && draftTimer) saveServerDraft();
  }

  function readDraft() {
    try {
      return JSON.parse(localStorage.getItem(`${storagePrefix}.draft`) || "null") || {};
    } catch {
      return {};
    }
  }

  function blankAssist() {
    return { use: null, area: "", focus: "", ideas: null, ideasFor: "" };
  }

  function blankForm() {
    return { name: "", description: "", share: 0, frequency: "", duration: "", digital: "", continuity: "", standardization: "" };
  }

  function formatDay(value) {
    return new Date(value).toLocaleDateString(t.locale, { day: "numeric", month: "long" });
  }

  function formatDate(value) {
    if (!value) return "";
    return new Date(value).toLocaleString(t.locale, { weekday: "short", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  }

  function recommended(task) {
    return task.status === "suitable" || task.status === "framework";
  }

  // Ohne "Weiss nicht" heisst "" schlicht "nicht angegeben" - im Formular wie vom Server
  function toForm(task) {
    return {
      name: task.name,
      description: task.description || "",
      share: task.share || 0,
      frequency: task.frequency || "",
      duration: task.duration || "",
      in_period: typeof task.in_period === "boolean" ? task.in_period : null,
      digital: task.digital || "",
      continuity: task.continuity || "",
      standardization: task.standardization || "",
      ...(typeof task.process === "string" ? { process: task.process, process_id: task.process_id || null } : {}),
    };
  }

  function fromForm(entry) {
    const value = (field) => (entry[field] === UNKNOWN ? "" : entry[field]);
    return {
      name: entry.name.trim(),
      description: entry.description.trim(),
      share: entry.share,
      frequency: value("frequency"),
      digital: value("digital"),
      continuity: value("continuity"),
      standardization: value("standardization"),
      duration: entry.duration || "",
      in_period: typeof entry.in_period === "boolean" ? entry.in_period : null,
      ...(typeof entry.process === "string" ? { process: entry.process, process_id: entry.process_id || null } : {}),
    };
  }

  async function load({ keepView = false } = {}) {
    try {
      const query = `participant=${encodeURIComponent(participant)}${invite ? `&invite=${encodeURIComponent(invite)}` : ""}`;
      const response = await request(`/api/damprojects/team-survey/${surveyId}?${query}`);
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "not found");
      const stageChanged = survey && survey.stage !== result.stage;
      survey = result;
      error = "";
      if (!keepView || stageChanged) restoreAnswers(stageChanged);
      schedulePoll();
    } catch {
      error = teamSurveyTexts[browserLanguage || "en"].notFound;
    } finally {
      loading = false;
    }
  }

  // Der neuere Zwischenstand gewinnt: dieser Browser oder (mit persoenlichem Link) der Server
  function newestDraft() {
    const local = readDraft();
    const server = survey.invite?.draft;
    if (server && (!local.savedAt || Date.parse(server.saved_at) > local.savedAt)) return server.data || {};
    return local;
  }

  function restoreAnswers(stageChanged) {
    const draft = newestDraft();
    const own = survey.own || {};
    sentElsewhere = Boolean(survey.invite?.done && !own[survey.stage]);
    if (survey.stage === "collect") {
      tasks = own.collect?.tasks?.map(toForm) || draft.tasks || [];
      form = draft.form || blankForm();
      assist = { ...blankAssist(), ...(draft.assist || {}) };
      processIndex = Number.isInteger(draft.processIndex) ? draft.processIndex : 0;
      view = own.collect || sentElsewhere ? "sent" : tasks.length ? "main" : "intro";
    } else if (survey.stage === "select") {
      const defaults = Object.fromEntries(allTasksOf(survey).map((task) => [task.key, { keep: recommended(task), comment: "" }]));
      const saved = own.select?.votes || draft.votes || {};
      votes = { ...defaults, ...Object.fromEntries(Object.entries(saved).filter(([key]) => key in defaults)) };
      const savedMissing = own.select?.missing || draft.missing;
      missing = Array.isArray(savedMissing) ? savedMissing.map(toForm) : []; // frueher Freitext
      form = blankForm();
      addingMissing = false;
      view = own.select || sentElsewhere ? "sent" : "intro";
    }
    lastSnapshot = JSON.stringify({ tasks, form, votes, missing, assist, processIndex });
    if (stageChanged && survey.stage === "select") notice = own.collect ? t.stageChangedSent : t.stageChanged;
  }

  function allTasksOf(currentSurvey) {
    return (currentSurvey.themes || []).flatMap((theme) => theme.tasks);
  }

  function schedulePoll() {
    clearTimeout(pollTimer);
    if (survey?.preparing) pollTimer = setTimeout(() => load({ keepView: true }), POLL_MS);
  }

  async function show(nextView) {
    view = nextView;
    notice = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
    await tick();
    nameInput?.focus({ preventScroll: true });
  }

  // --- Stufe collect ---------------------------------------------------------------------------

  // Erneutes Antippen waehlt ab - ohne "Weiss nicht" der Weg zurueck zu "keine Angabe"
  function pick(field, value) {
    form = { ...form, [field]: form[field] === value ? "" : value };
  }

  async function useSuggestion(name) {
    form = { ...blankForm(), name };
    editIndex = -1;
    formStep = 1;
    await tick();
    document.getElementById("team-survey-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  async function createIdeas() {
    assistLoading = true;
    assistNote = "";
    const context = `${assist.area.trim()}|${assist.focus.trim()}`;
    try {
      const response = await request(`/api/damprojects/team-survey/${surveyId}/ideas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participant, language, area: assist.area, focus: assist.focus }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.code || "failed");
      assist = { ...assist, ideas: result.ideas, ideasFor: context, shown: IDEA_AREAS.flatMap((area) => result.ideas[area] || []) };
    } catch {
      assist = { ...assist, ideas: null, ideasFor: "" };
      assistNote = t.assist.error;
    } finally {
      assistLoading = false;
    }
  }

  // Neue Ideen ohne Wiederholung: alles bisher Gezeigte und Eingetragene geht als Ausschluss mit
  async function moreIdeas() {
    moreLoading = true;
    try {
      const shown = [...(assist.shown || []), ...tasks.map((task) => task.name)];
      const response = await request(`/api/damprojects/team-survey/${surveyId}/ideas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participant, language, area: assist.area, focus: assist.focus, exclude: shown }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.code || "failed");
      // je Taetigkeit die neuen Ideen; wo Pax AI nichts Neues fand, bleiben die bisherigen stehen
      const ideas = Object.fromEntries(IDEA_AREAS.map((area) => [area, result.ideas[area]?.length ? result.ideas[area] : assist.ideas[area] || []]));
      assist = { ...assist, ideas, shown: [...(assist.shown || []), ...IDEA_AREAS.flatMap((area) => result.ideas[area] || [])] };
    } catch {
      notice = t.assist.error;
    } finally {
      moreLoading = false;
    }
  }

  // "Aufgaben sammeln": mit Ja erst die Ideen holen (neu nur, wenn sich Bereich oder Zustaendigkeit geaendert haben)
  async function startCollect() {
    if (assist.use === true && assist.ideasFor !== `${assist.area.trim()}|${assist.focus.trim()}`) await createIdeas();
    if (assist.use !== true) assist = { ...assist, use: false };
    show("main");
  }

  // Ohne Ablauf einmal gezielt nachfragen statt nur den Namen zu uebernehmen; der zweite Klick uebernimmt trotzdem
  const FEW_WORDS = 3; // ein kurzer Satz genuegt (Rueckmeldung 02.10.) - nachgefragt wird nur bei fast leerem Feld

  function formNext() {
    if (textMissing) return;
    if (!descAsked && form.description.trim().split(/\s+/).filter(Boolean).length < FEW_WORDS) {
      descAsked = true;
      return;
    }
    descAsked = false;
    formStep = 2;
  }

  function commitForm() {
    if (missingCount) return;
    // neue Aufgaben gehoeren zum gerade gefragten Prozess; bearbeitete behalten ihren
    const scope = editIndex < 0 && currentProcess ? { process: currentProcess.name, process_id: currentProcess.id } : {};
    const entry = { ...form, ...scope, name: form.name.trim(), description: form.description.trim() };
    tasks = editIndex < 0 ? [...tasks, entry] : tasks.map((task, index) => (index === editIndex ? entry : task));
    descAsked = false;
    formStep = 1;
    form = blankForm();
    editIndex = -1;
    nameInput?.focus();
  }

  function editTask(index) {
    const step = processSteps.findIndex((process) => process.key === processOf(tasks[index]));
    if (step >= 0) processIndex = step;
    form = { ...tasks[index] };
    editIndex = index;
    formStep = 1;
    document.getElementById("team-survey-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // Entfernen immer mit Rueckfrage (Rueckmeldung 04.10.: ein Fehlklick loeschte sofort)
  let removal = null;

  function askRemove(name, run) {
    removal = { name, run };
  }

  function confirmRemove() {
    removal?.run();
    removal = null;
  }

  function removeTask(index) {
    tasks = tasks.filter((_, taskIndex) => taskIndex !== index);
    if (editIndex === index) cancelEdit();
  }

  function cancelEdit() {
    form = blankForm();
    editIndex = -1;
    descAsked = false;
    formStep = 1;
  }

  // Themen in der Reihenfolge von Pax AI; was (noch) keinem Thema angehoert, kommt ans Ende - ohne Ordnung eine Liste.
  // items: [{ task, index }] mit dem Index in tasks (Bearbeiten, Entfernen)
  function themesOf(items, { names, themes }) {
    const withTheme = items.map((item) => ({ ...item, theme: names[item.task.name.toLocaleLowerCase()] || "" }));
    const groups = themes.map((name) => ({ name, items: withTheme.filter((item) => item.theme === name) }));
    const rest = withTheme.filter((item) => !item.theme);
    return [...groups.filter((group) => group.items.length), ...(rest.length ? [{ name: groups.length ? t.collect.reviewOther : "", items: rest }] : [])];
  }

  const NO_GROUPING = { names: {}, themes: [] };
  const indexed = (list) => list.map((task, index) => ({ task, index }));
  // Ein Bereich heisst "L1: L2", wenn die Einrichtung einen Teilprozess mit seinem Prozess hinterlegt hat
  const areaLabel = (process) => (!process.name ? t.collect.otherTitle : process.parent ? `${process.parent}: ${process.name}` : process.name);

  // Bereich der Aufgabe - ueber die Id, sonst den Namen; unbekannte (z. B. spaeter entfernte) zaehlen zu "Sonstige Aufgaben"
  function processOf(task) {
    const step = processSteps.find((process) => process.name && (process.id && task.process_id ? process.id === task.process_id : process.name === task.process));
    return step ? step.key : "";
  }

  function areaSections(list) {
    return processSteps
      .map((process) => ({ label: areaLabel(process), key: process.key, items: indexed(list).filter(({ task }) => processOf(task) === process.key) }))
      .filter((section) => section.items.length);
  }

  async function requestGroups(list, area = "") {
    const response = await request(`/api/damprojects/team-survey/${surveyId}/groups`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ participant, language, area, tasks: list.map(({ name, description }) => ({ name, description })) }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.code || "failed");
    return {
      names: Object.fromEntries(result.themes.flatMap((theme) => theme.tasks.map((name) => [name.toLocaleLowerCase(), theme.name]))),
      themes: result.themes.map((theme) => theme.name),
    };
  }

  const grouped = (list, { names }) => list.every((task) => names[task.name.toLocaleLowerCase()]);

  // Mit Bereichen ordnet Pax AI jeden Bereich fuer sich; nur neu, was sich geaendert hat
  async function groupForReview() {
    if (!tasks.length) return;
    const open = processSteps.length
      ? areaSections(tasks).filter((section) => !grouped(section.items.map(({ task }) => task), areaGrouping[section.key] || NO_GROUPING))
      : grouped(tasks, grouping) ? [] : [null];
    if (!open.length) return;
    groupingLoading = true;
    await Promise.all(open.map(async (section) => {
      try {
        if (!section) {
          grouping = await requestGroups(tasks);
        } else {
          // erst warten, dann den aktuellen Stand erweitern - parallele Bereiche ueberschreiben sich sonst
          const result = await requestGroups(section.items.map(({ task }) => task), section.label);
          areaGrouping = { ...areaGrouping, [section.key]: result };
        }
      } catch {
        // ohne Themen bleibt es eine Liste - nichts geht verloren
      }
    }));
    groupingLoading = false;
  }

  function setInPeriod(index, value) {
    tasks = tasks.map((task, taskIndex) => (taskIndex === index ? { ...task, in_period: task.in_period === value ? null : value } : task));
  }

  async function editFromReview(index) {
    await show("main");
    editTask(index);
  }

  // Enter heisst im ganzen Formular "uebernehmen" - auch auf einem Chip, der sonst erneut ausloesen wuerde
  function formKeydown(event) {
    if (event.key === "Enter" && event.target.tagName !== "TEXTAREA") {
      event.preventDefault();
      if (survey.stage === "select") addMissing();
      else if (formStep === 1) formNext();
      else commitForm();
    }
  }

  // --- Senden ----------------------------------------------------------------------------------

  async function send(body) {
    if (preview) {
      show("sent");
      return;
    }
    saving = true;
    notice = "";
    // ein noch geplanter Zwischenstand kaeme nach dem Absenden an und erschiene als Fehler (Test 04.10.)
    clearTimeout(draftTimer);
    draftTimer = null;
    try {
      const response = await request(`/api/damprojects/team-survey/${surveyId}/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participant, invite, stage: survey.stage, ...body }),
      });
      const result = await response.json();
      if (response.status === 409 && result.code === "already_submitted") {
        sentElsewhere = true;
        show("sent");
        return;
      }
      if (response.status === 409 && result.code === "stage_changed") {
        await load();
        return;
      }
      if (!response.ok) throw new Error(result.message);
      await load({ keepView: true });
      show("sent");
    } catch {
      notice = t.saveError;
    } finally {
      saving = false;
    }
  }

  // Eine angefangene Aufgabe kommt vor dem Wechsel noch in die Liste des aktuellen Prozesses
  async function goProcess(nextIndex) {
    if (formStarted && editIndex < 0) {
      if (missingCount) {
        notice = t.collect.pendingForm;
        return;
      }
      commitForm();
    } else if (editIndex >= 0) {
      cancelEdit();
    }
    processIndex = nextIndex;
    await show("main");
  }

  // Erst pruefen, dann senden (Rueckmeldung 02.10.): eine angefangene Aufgabe kommt vorher noch in die Liste
  function reviewTasks() {
    if (formStarted) {
      if (missingCount) {
        notice = t.collect.pendingForm;
        return;
      }
      commitForm(); // auf dem Weg zur Pruefseite ohne Nachfrage und ohne Kurzfragen
    }
    // auch ohne Aufgaben weiter: wer in seinen Bereichen nichts hat, schickt eine leere Liste (Rueckmeldung 07.10.)
    show("review");
    groupForReview();
  }

  // Die Frage nach der Erhebungsphase ist die zentrale (Rueckmeldung 04.10.): einmal auf offene hinweisen,
  // beim zweiten Klick trotzdem senden - wie beim fehlenden Ablauf
  let periodAsked = false;
  $: periodOpen = period ? tasks.filter((task) => typeof task.in_period !== "boolean").length : 0;

  async function submitTasks() {
    if (periodOpen && !periodAsked) {
      periodAsked = true;
      await tick();
      document.querySelector(".period_question.open")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    send({ tasks: tasks.map(fromForm) });
  }

  // Fehlt eine Aufgabe, wird sie wie in Stufe collect eingeordnet - der Framework Assistant braucht dieselben Angaben
  async function openMissing() {
    form = blankForm();
    addingMissing = true;
    await tick();
    nameInput?.focus();
  }

  function addMissing() {
    if (missingCount) return;
    missing = [...missing, { ...form, name: form.name.trim(), description: form.description.trim() }];
    form = blankForm();
    addingMissing = false;
  }

  function removeMissing(index) {
    missing = missing.filter((_, taskIndex) => taskIndex !== index);
  }

  function submitVotes() {
    if (addingMissing && formStarted) {
      if (missingCount) {
        notice = t.collect.pendingForm;
        return;
      }
      addMissing();
    }
    send({ votes, missing: missing.map(fromForm) });
  }

  onMount(() => {
    // Tab-Titel "Task Listing • paxray" ueber PageTitle wie auf den Plattformseiten (Rueckmeldung 07.10.)
    pageTitleStore.set({ pathname: window.location.pathname, section: "", breadcrumb: "Task Listing" });
    load();
    document.addEventListener("visibilitychange", flushDraft);
  });
  onDestroy(() => {
    clearTimeout(pollTimer);
    clearTimeout(draftTimer);
    document.removeEventListener("visibilitychange", flushDraft);
  });
</script>

<div class="survey" class:dark lang={language}>
<header class="topbar">
  <span class="topbar_logo"><SideNavHeaderLogo full /></span>
  {#if survey && survey.stage !== "done"}
    <span class="topbar_step" aria-live="polite">{progressStep + 1} / {progressSteps.length} · {progressSteps[progressStep]}</span>
  {/if}
  <button type="button" class="theme_toggle" class:dark aria-pressed={dark} aria-label={dark ? t.lightMode : t.darkMode} title={dark ? t.lightMode : t.darkMode} on:click={toggleTheme}></button>
</header>

<main class="page">
  {#if loading}
    <div class="state"><Loading withOverlay={false} /></div>
  {:else if error}
    <!-- statt einer Fehlermeldung ein ruhiger Hinweis (Rueckmeldung 04.10.) -->
    <section class="card state_card"><div class="notice_card" role="status"><span class="notice_icon" aria-hidden="true">i</span><p>{error}</p></div></section>
  {:else if survey.preparing}
    <section class="card state_card">
      <Loading withOverlay={false} small />
      <h2>{t.preparing}</h2>
      <p>{t.preparingHint}</p>
    </section>
  {:else if survey.stage === "collect"}
    {#if view === "intro"}
      <!-- Startseite: es geht nur um die Aufgaben fuer das Framework - eine klare Aufforderung und drei Zusagen, die
           Vertrauen schaffen (Zeitleiste mit den spaeteren Task-Mining-Schritten entfernt, 06.10.) -->
      <section class="card intro hero">
        <!-- die Abteilung statt des Teams (Rueckmeldung 07.10.); ohne Abteilung bleibt es beim Team -->
        {#if survey?.department || survey?.team}<span class="eyebrow">{survey.department ? `${t.departmentLabel}: ${survey.department}` : survey.team}</span>{/if}
        <h1>{t.collect.introTitle}</h1>
        <p class="lead intro_why">{t.collect.introLead}</p>
        <div class="today_card">
          <div class="today_art" aria-hidden="true"><svg viewBox="0 0 120 80"><rect class="ill-card" x="28" y="10" width="54" height="60" rx="7" /><rect class="ill-box" x="37" y="22" width="7" height="7" rx="2" /><rect class="ill-box" x="37" y="36" width="7" height="7" rx="2" /><rect class="ill-box" x="37" y="50" width="7" height="7" rx="2" /><path class="ill-line" d="M50 25.5h22M50 39.5h18M50 53.5h12" /><path class="ill-accent-line" d="M73 63l17-17 6 6-17 17-8 2z" /></svg></div>
          <div class="today_body">
            <span class="tl_when now">{t.collect.introCardLabel}</span>
            <h2>{t.collect.introCardTitle}</h2>
            <p>{t.collect.introCardText}</p>
            <div class="meta">
              <span class="meta_item time">{t.collect.duration}</span>
              <span class="meta_item date">{late ? t.lateOpen : t.openUntil(deadline)}</span>
            </div>
          </div>
          <div class="today_action"><Button variant="secondary" size="sm" text={t.start} on:click={() => show("assist")} /></div>
        </div>
        <ul class="trust">
          <li class="meta_item lock">{t.anonymous}</li>
          <li class="meta_item users">{t.collect.introTrustTeam}</li>
          <li class="meta_item together">{t.collect.introTrustTogether}</li>
        </ul>
      </section>
    {:else if view === "assist"}
      <section class="card intro">
        <!-- Erst die Frage nach Pax AI; nur mit "Ja" die Fragen zum Kontext, die die Ideen brauchen (Rueckmeldung 02.10.) -->
        <h1>{t.assist.title}</h1>
        <p class="lead">{t.assist.lead}</p>
        <div class="choice" role="radiogroup" aria-label={t.assist.title}>
          <label class="radio option spark"><input type="radio" bind:group={assist.use} value={true} /><span>{t.assist.yes}</span></label>
          <label class="radio option pen"><input type="radio" bind:group={assist.use} value={false} /><span>{t.assist.no}</span></label>
        </div>

        {#if assist.use === true}
          <div class="context_card">
          <h2 class="section_title">{t.collect.areaTitle}</h2>
          <div class="text_fields single intro_fields">
            <label><span>{t.collect.area}</span><input bind:value={assist.area} maxlength="200" placeholder={t.collect.areaPlaceholder} /></label>
            <label>
              <span>{t.collect.focus}</span>
              <textarea bind:value={assist.focus} rows="4" maxlength="600" placeholder={t.collect.focusPlaceholder}></textarea>
              <em class="field_hint">{t.collect.focusHelp}</em>
            </label>
            <em class="field_hint">{t.collect.areaNote}</em>
          </div>
          </div>
        {/if}
        <footer>
          <Button variant="secondary" size="sm" text={t.back} disabled={assistLoading} on:click={() => show("intro")} />
          <div class="submit_group">
            {#if assistLoading}<span class="assist_loading"><Loading withOverlay={false} small />{t.assist.loading}</span>{/if}
            <Button variant="secondary" size="sm" text={t.collect.startCollect} disabled={assistLoading} on:click={startCollect} />
          </div>
        </footer>
      </section>
    {:else if view === "main"}
      <section class="card">
        {#if currentProcess}
          <!-- Alle Bereiche auf einen Blick, der aktuelle hervorgehoben (Rueckmeldung 07.10.: klare Fuehrung bei L1 + L2) -->
          <p class="area_intro">{t.collect.areasIntro(processes.length)}</p>
          <nav class="area_steps" aria-label={t.collect.areas}>
            {#each processSteps as process, index}
              <button type="button" class="area_step" class:current={index === processIndex} class:done={index < processIndex}
                      aria-current={index === processIndex ? "step" : undefined} on:click={() => index !== processIndex && goProcess(index)}>
                <span class="area_number">{index < processIndex ? "✓" : process.name ? index + 1 : "+"}</span>
                <span class="area_name">{areaLabel(process)}</span>
                {#if areaCounts[process.key]}<b>{areaCounts[process.key]}</b>{/if}
              </button>
            {/each}
          </nav>
        {/if}
        <div class="title_row">
          <div>
            {#if currentProcess}
              <span class="eyebrow">{currentProcess.name ? t.collect.processStep(processIndex + 1, processes.length) : t.collect.otherStep}</span>
              <!-- der Bereich selbst als Ueberschrift, sein Prozess (L1) klein darueber; die Frage steht im Hinweis -->
              {#if currentProcess.parent}<span class="area_parent">{currentProcess.parent}</span>{/if}
              <h1>{currentProcess.name || t.collect.otherTitle}</h1>
              <p class="lead">{currentProcess.name ? t.collect.processLead : t.collect.otherLead}</p>
            {:else}
              <h1>{t.collect.title2}</h1>
              <p class="lead">{t.collect.lead2}</p>
            {/if}
          </div>
          <strong class="collected">{t.collect.collected(tasks.length)}</strong>
        </div>

        <div class="model_task">
          <span>{t.collect.exampleTitle}</span>
          <div class="model_text">
            <strong>{model[0]}</strong>
            <em>„{model[2]}“</em>
          </div>
        </div>

        {#if assist.use}
          <div class="ideas_box">
            <div class="ideas_head">
              <strong>{aiIdeas ? t.assist.ideasTitle : t.assist.examplesTitle}</strong>
              <button type="button" class="link" aria-expanded={ideasOpen} on:click={() => (ideasOpen = !ideasOpen)}>{ideasOpen ? t.assist.hide : t.assist.show}</button>
            </div>
            {#if ideasOpen}
              {#if assistNote}<p class="notice">{assistNote}</p>{/if}
              <p class="ideas_hint">{t.assist.ideasHint}</p>
              <div class="chips idea_kinds" role="tablist">
                {#each ideaKinds as kind}
                  <button type="button" role="tab" class="chip" class:selected={kind === ideaKind} aria-selected={kind === ideaKind} on:click={() => (ideaKind = kind)}>{t.collect.ideas.areas[kind]}</button>
                {/each}
              </div>
              <div class="chips idea_list">
                {#each ideaNames as name}
                  <button type="button" class="chip suggestion" disabled={taken.has(name.toLocaleLowerCase())} on:click={() => useSuggestion(name)}>
                    {taken.has(name.toLocaleLowerCase()) ? "✓" : "+"} {name}
                  </button>
                {/each}
                <!-- neue Vorschlaege immer als letzter Eintrag derselben Reihe (Rueckmeldung 04.10.) -->
                {#if aiIdeas}
                  {#if moreLoading}<span class="assist_loading regen_loading"><Loading withOverlay={false} small />{t.assist.moreLoading}</span>
                  {:else}<button type="button" class="regen" on:click={moreIdeas}>↻ {t.assist.more}</button>{/if}
                {/if}
              </div>
            {/if}
          </div>
        {/if}

        {#if suggestions.length}
          <div class="suggestions">
            <p>{t.collect.suggestions}</p>
            <div class="chips">
              {#each suggestions as name}
                <button type="button" class="chip suggestion" on:click={() => useSuggestion(name)}>+ {name}</button>
              {/each}
            </div>
          </div>
        {/if}

        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div class="task_form" id="team-survey-form" class:editing={editIndex >= 0} on:keydown={formKeydown}>
          {#if formStep === 1}
            <div class="text_fields">
              <label>
                <span>{t.collect.name}</span>
                <input bind:this={nameInput} bind:value={form.name} maxlength="120" placeholder={t.collect.namePlaceholder(model[0])} list="team-survey-names" autocomplete="off"
                       on:focus={() => (nameLeft = false)} on:blur={() => (nameLeft = true)} />
                <datalist id="team-survey-names">{#each suggestions as name}<option value={name}></option>{/each}</datalist>
                {#if coach}<em class="field_hint coach">{coach}</em>{/if}
              </label>
              <label>
                <span>{t.collect.description}</span>
                <textarea bind:value={form.description} rows="2" maxlength="300" placeholder={t.collect.descriptionPlaceholder(model[2])}></textarea>
                <em class="field_hint">{t.collect.descriptionHelp}</em>
              </label>
            </div>
            <div class="form_actions">
              {#if descAsked && !textMissing}<em class="coach">{t.collect.askDescription}</em>{/if}
              {#if editIndex >= 0}<Button variant="secondary" size="sm" text={t.collect.cancelEdit} on:click={cancelEdit} />{/if}
              <Button variant="secondary" size="sm" text={t.collect.next} disabled={Boolean(textMissing)} on:click={formNext} />
            </div>
          {:else}
            <div class="questions">
              <div class="questions_head">
                <strong>{t.collect.classifyFor(form.name.trim())} <em>{t.collect.classifyHint}</em></strong>
              </div>
              <div class="question" class:answered={form.frequency}>
                <span>{t.collect.frequency}</span>
                <div class="chips">
                  {#each options.frequency as value}
                    <button type="button" class="chip" class:selected={form.frequency === value} aria-pressed={form.frequency === value} on:click={() => pick("frequency", value)}>{t.values[value]}</button>
                  {/each}
                </div>
              </div>
              <div class="question" class:answered={form.duration}>
                <span>{t.collect.taskDuration} <em class="optional">{t.optional}</em></span>
                <div class="chips">
                  {#each DURATIONS as value}
                    <button type="button" class="chip" class:selected={form.duration === value} aria-pressed={form.duration === value} on:click={() => pick("duration", value)}>{t.values[value]}</button>
                  {/each}
                </div>
              </div>
              {#each ["digital", "continuity", "standardization"] as field}
                <div class="question" class:answered={form[field]}>
                  <span>{t.collect[field]}{#if OPTIONAL_FIELDS.includes(field)}{" "}<em class="optional">{t.optional}</em>{/if}</span>
                  <div class="chips">
                    {#each options[field] as value}
                      <button type="button" class="chip" class:selected={form[field] === value} aria-pressed={form[field] === value} on:click={() => pick(field, value)}>{t.values[value]}</button>
                    {/each}
                  </div>
                </div>
              {/each}
            </div>
            <div class="form_actions">
              {#if missingCount}<em class="coach">{t.collect.missing(missingCount)}</em>{/if}
              <Button variant="secondary" size="sm" text={t.back} on:click={() => (formStep = 1)} />
              <Button variant="secondary" size="sm" text={editIndex >= 0 ? t.collect.update : `+ ${t.collect.add}`} disabled={Boolean(missingCount)} on:click={commitForm} />
            </div>
          {/if}
        </div>

        <h2 class="list_title">{currentProcess?.name ? t.collect.listIn(areaLabel(currentProcess), listedTasks.length) : t.collect.list(listedTasks.length)}</h2>
        {#if listedTasks.length}
          <ol class="task_list">
            {#each listedTasks as { task, index }, position (index)}
              <li class="task_row" class:editing={editIndex === index}>
                <span class="number">{position + 1}</span>
                <div>
                  <strong>{task.name}</strong>
                  {#if task.description}<p>{task.description}</p>{/if}
                </div>
                <div class="facts">
                  {#if task.frequency && task.frequency !== UNKNOWN}<span>{t.values[task.frequency]}</span>{/if}
                  {#if task.duration}<span>{t.values[task.duration]}</span>{/if}
                  {#if t.facts[task.digital]}<span>{t.facts[task.digital]}</span>{/if}
                  {#if t.facts[task.continuity]}<span>{t.facts[task.continuity]}</span>{/if}
                  {#if t.facts[task.standardization]}<span>{t.facts[task.standardization]}</span>{/if}
                  {#if task.share}<span>{t.collect.weights[weightOf(task.share)]}</span>{/if}
                </div>
                <div class="row_actions">
                  <button type="button" class="icon_btn" title={t.collect.edit} aria-label={t.collect.edit} on:click={() => editTask(index)}><EditIcon /></button>
                  <button type="button" class="icon_btn danger" title={t.collect.remove} aria-label={t.collect.remove} on:click={() => askRemove(task.name, () => removeTask(index))}><svg class="trash" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V4.5h6V7M6.5 7l.8 12.5a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7M10 11v6M14 11v6" /></svg></button>
                </div>
              </li>
            {/each}
          </ol>
        {:else}
          <p class="empty">{t.collect.empty}</p>
        {/if}

        {#if notice}<p class="notice">{notice}</p>{/if}
        {#if draftState && !preview}<p class="draft_state">✓ {invite ? t.draftSavedEverywhere : t.draftSaved}</p>{/if}
        <footer>
          <Button variant="secondary" size="sm" text={t.back} on:click={() => (processIndex > 0 ? goProcess(processIndex - 1) : show("assist"))} />
          {#if currentProcess && processIndex < processSteps.length - 1}
            <Button variant="secondary" size="sm" text={t.collect.nextProcess(areaLabel(processSteps[processIndex + 1]))} on:click={() => goProcess(processIndex + 1)} />
          {:else}
            <Button variant="secondary" size="sm" text={t.collect.review} on:click={reviewTasks} />
          {/if}
        </footer>
      </section>
    {:else if view === "review"}
      <section class="card">
        <h1>{t.collect.reviewTitle}</h1>
        <p class="lead">{t.collect.reviewLead}</p>
        {#if !processSteps.length}<h2 class="list_title">{t.collect.list(tasks.length)}</h2>{/if}
        {#if period}
          <div class="period_callout">
            <strong>{t.collect.periodTitle}</strong>
            <p>{t.collect.periodLabel}: <b>{t.collect.periodRange(period[0], period[1])}</b></p>
            <p>{t.collect.periodHint}</p>
          </div>
        {/if}
        {#if groupingLoading}<p class="assist_loading"><Loading withOverlay={false} small />{t.collect.reviewGrouping}</p>{/if}
        {#if !tasks.length}<p class="empty">{t.collect.noTasksReview}</p>{/if}
        {#each reviewSections as section (section.key)}
          {#if section.label}<h2 class="area_title"><span>{section.label}</span> <b>{section.items.length}</b></h2>{/if}
          {#each section.themes as theme}
          {#if theme.name}<h3 class="theme">{theme.name} <span>{theme.items.length}</span></h3>{/if}
          <ol class="task_list">
            {#each theme.items as { task, index } (index)}
              <li class="task_row review_row">
                <span class="number">{index + 1}</span>
                <div>
                  <strong>{task.name}</strong>
                  {#if task.description}<p>{task.description}</p>{/if}
                </div>
                <div class="row_actions">
                  <button type="button" class="icon_btn" title={t.collect.edit} aria-label={t.collect.edit} on:click={() => editFromReview(index)}><EditIcon /></button>
                  <button type="button" class="icon_btn danger" title={t.collect.remove} aria-label={t.collect.remove} on:click={() => askRemove(task.name, () => removeTask(index))}><svg class="trash" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V4.5h6V7M6.5 7l.8 12.5a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7M10 11v6M14 11v6" /></svg></button>
                </div>
                {#if period}
                  <div class="period_question" class:open={periodAsked && typeof task.in_period !== "boolean"}>
                    <span>{t.collect.inPeriod}</span>
                    <div class="toggle">
                      <button type="button" class:selected={task.in_period === true} aria-pressed={task.in_period === true} on:click={() => setInPeriod(index, true)}>{t.select.yes}</button>
                      <button type="button" class:selected={task.in_period === false} aria-pressed={task.in_period === false} on:click={() => setInPeriod(index, false)}>{t.select.no}</button>
                    </div>
                  </div>
                {/if}
              </li>
            {/each}
          </ol>
          {/each}
        {/each}
        <div class="review_more">
          <p>{t.collect.reviewMore}</p>
          <button type="button" class="chip suggestion" on:click={() => show("main")}>+ {t.collect.addAnother}</button>
        </div>
        {#if periodAsked && periodOpen}<p class="period_open">{t.collect.periodOpen(periodOpen)}</p>{/if}
        {#if notice}<p class="notice">{notice}</p>{/if}
        <footer>
          <Button variant="secondary" size="sm" text={t.back} on:click={() => show("main")} />
          <Button variant="secondary" size="sm" text={saving ? t.collect.submitting : t.collect.submit} disabled={saving} on:click={submitTasks} />
        </footer>
      </section>
    {:else}
      <section class="card state_card sent">
        <span class="check" aria-hidden="true"></span>
        <h2>{t.collect.sentTitle}</h2>
        {#if sentElsewhere || late}
          <p>{sentElsewhere ? t.alreadySent : t.lateOpen}</p>
        {:else}
          <p>{t.collect.sentText}</p>
          <p class="hint">{t.collect.sentChange(deadline)}</p>
        {/if}
        {#if !sentElsewhere}<Button variant="secondary" size="sm" text={t.collect.sentEdit} on:click={() => show("main")} />{/if}
      </section>
    {/if}
  {:else if survey.stage === "select"}
    {#if view === "intro"}
      <section class="card intro">
        {#if notice}<p class="notice">{notice}</p>{/if}
        <h1>{t.select.title}</h1>
        {#if survey.mode === "select"}
          <p class="lead">{t.select.leadFramework(allTasks.length, themes.length)}</p>
          <p class="lead">{t.select.howFramework} {t.select.meaning}</p>
        {:else}
          <p class="lead">{t.select.lead(allTasks.length, themes.length)}</p>
          <p class="lead">{t.select.how} {t.select.meaning}</p>
        {/if}
        <div class="meta">
          <span class="meta_item time">{t.select.duration}</span>
          <span class="meta_item date">{late ? t.lateOpen : t.openUntil(deadline)}</span>
          <span class="meta_item lock">{t.anonymous}</span>
        </div>
        {#if survey.mode !== "select"}
          <div class="example">
            <p class="example_label">{t.select.criteria}</p>
            <ul class="criteria">{#each t.select.criteriaList as item}<li>{item}</li>{/each}</ul>
          </div>
        {/if}
        <footer><span></span><Button variant="secondary" size="sm" text={t.start} on:click={() => show("main")} /></footer>
      </section>
    {:else if view === "main"}
      <section class="card">
        <div class="title_row">
          <div>
            <h1>{t.select.title2}</h1>
            <p class="lead">{survey.mode === "select" ? t.select.howFramework : t.select.how}</p>
          </div>
        </div>
        <div class="chips filters">
          {#each Object.entries(t.select.filters) as [key, label]}
            {#if key === "all" || filterCounts[key]}
              <button type="button" class="chip" class:selected={filter === key} aria-pressed={filter === key} on:click={() => (filter = key)}>{label} <b>{filterCounts[key] || 0}</b></button>
            {/if}
          {/each}
        </div>
        {#each themes as theme}
          {@const visible = theme.tasks.filter((task) => filter === "all" || task.status === filter)}
          {#if visible.length}
            <h2 class="theme">{theme.name} <span>{theme.tasks.length}</span></h2>
            <ol class="task_list">
              {#each visible as task (task.key)}
                <li class="task_row vote_row">
                  <div class="task_text">
                    <strong>{task.name} {#if task.mentions}<span class="badge">{t.select.mentions(task.mentions)}</span>{/if}</strong>
                    {#if task.description}<p>{task.description}</p>{/if}
                    <div class="facts">
                      {#if task.frequency}<span>{t.values[task.frequency]}</span>{/if}
                      {#if task.duration}<span>{t.values[task.duration]}</span>{/if}
                      {#if task.in_period?.yes || task.in_period?.no}<span>{t.select.inPeriod(task.in_period.yes, task.in_period.no)}</span>{/if}
                      {#if t.facts[task.digital]}<span>{t.facts[task.digital]}</span>{/if}
                      {#if t.facts[task.continuity]}<span>{t.facts[task.continuity]}</span>{/if}
                      {#if t.facts[task.standardization]}<span>{t.facts[task.standardization]}</span>{/if}
                      {#if task.share}<span>{t.select.share(task.share)}</span>{/if}
                    </div>
                  </div>
                  <div class="assessment {task.status}">
                    <strong>{t.status[task.status]}</strong>
                    <span>{t.reasons[task.reason]}</span>
                  </div>
                  <div class="decision">
                    <span>{t.select.question}</span>
                    <div class="toggle">
                      <button type="button" class:selected={votes[task.key]?.keep} aria-pressed={Boolean(votes[task.key]?.keep)} on:click={() => (votes = { ...votes, [task.key]: { ...votes[task.key], keep: true } })}>{t.select.yes}</button>
                      <button type="button" class:selected={votes[task.key] && !votes[task.key].keep} aria-pressed={Boolean(votes[task.key] && !votes[task.key].keep)} on:click={() => (votes = { ...votes, [task.key]: { ...votes[task.key], keep: false } })}>{t.select.no}</button>
                    </div>
                    {#if openComments[task.key] || votes[task.key]?.comment}
                      <textarea rows="2" maxlength="500" placeholder={t.select.commentPlaceholder} value={votes[task.key]?.comment || ""} on:input={(event) => (votes = { ...votes, [task.key]: { ...votes[task.key], comment: event.currentTarget.value } })}></textarea>
                    {:else}
                      <button type="button" class="link" on:click={() => (openComments = { ...openComments, [task.key]: true })}>+ {t.select.comment}</button>
                    {/if}
                  </div>
                </li>
              {/each}
            </ol>
          {/if}
        {/each}
        <div class="missing">
          <h2 class="list_title">{t.select.missing} <em>{t.optional}</em></h2>
          {#if missing.length}
            <ol class="task_list">
              {#each missing as task, index}
                <li class="task_row missing_row">
                  <div>
                    <strong>{task.name}</strong>
                    {#if task.description}<p>{task.description}</p>{/if}
                  </div>
                  <div class="facts">
                    {#if task.frequency && task.frequency !== UNKNOWN}<span>{t.values[task.frequency]}</span>{/if}
                    {#if task.duration}<span>{t.values[task.duration]}</span>{/if}
                    {#if t.facts[task.digital]}<span>{t.facts[task.digital]}</span>{/if}
                    {#if t.facts[task.continuity]}<span>{t.facts[task.continuity]}</span>{/if}
                    {#if t.facts[task.standardization]}<span>{t.facts[task.standardization]}</span>{/if}
                    {#if task.share}<span>{t.collect.weights[weightOf(task.share)]}</span>{/if}
                  </div>
                  <button type="button" class="link" on:click={() => askRemove(task.name, () => removeMissing(index))}>{t.collect.remove}</button>
                </li>
              {/each}
            </ol>
          {/if}
          {#if addingMissing}
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div class="task_form" id="team-survey-form" on:keydown={formKeydown}>
            <div class="text_fields">
              <label>
                <span>{t.collect.name}</span>
                <input bind:this={nameInput} bind:value={form.name} maxlength="120" placeholder={t.collect.namePlaceholder(model[0])} autocomplete="off"
                       on:focus={() => (nameLeft = false)} on:blur={() => (nameLeft = true)} />
                
              </label>
              <label>
                <span>{t.collect.description}</span>
                <textarea bind:value={form.description} rows="2" maxlength="300" placeholder={t.collect.descriptionPlaceholder(model[2])}></textarea>
                <em class="field_hint">{t.collect.descriptionHelp}</em>
              </label>
            </div>
            <div class="questions">
              <div class="questions_head">
                <strong>{t.collect.classify} <em>{t.collect.classifyHint}</em></strong>
              </div>
              <div class="question" class:answered={form.frequency}>
                <span>{t.collect.frequency}</span>
                <div class="chips">
                  {#each options.frequency as value}
                    <button type="button" class="chip" class:selected={form.frequency === value} aria-pressed={form.frequency === value} on:click={() => pick("frequency", value)}>{t.values[value]}</button>
                  {/each}
                </div>
              </div>
              <div class="question" class:answered={form.duration}>
                <span>{t.collect.taskDuration} <em class="optional">{t.optional}</em></span>
                <div class="chips">
                  {#each DURATIONS as value}
                    <button type="button" class="chip" class:selected={form.duration === value} aria-pressed={form.duration === value} on:click={() => pick("duration", value)}>{t.values[value]}</button>
                  {/each}
                </div>
              </div>
              {#each ["digital", "continuity", "standardization"] as field}
                <div class="question" class:answered={form[field]}>
                  <span>{t.collect[field]}{#if OPTIONAL_FIELDS.includes(field)}{" "}<em class="optional">{t.optional}</em>{/if}</span>
                  <div class="chips">
                    {#each options[field] as value}
                      <button type="button" class="chip" class:selected={form[field] === value} aria-pressed={form[field] === value} on:click={() => pick(field, value)}>{t.values[value]}</button>
                    {/each}
                  </div>
                </div>
              {/each}
            </div>
              <div class="form_actions">
                {#if formStarted && missingCount}<em>{t.collect.missing(missingCount)}</em>{/if}
                <Button variant="secondary" size="sm" text={t.collect.cancelEdit} on:click={() => { addingMissing = false; form = blankForm(); }} />
                <Button variant="secondary" size="sm" text={`+ ${t.select.addMissing}`} disabled={Boolean(missingCount)} on:click={addMissing} />
              </div>
            </div>
          {:else}
            <button type="button" class="chip suggestion" on:click={openMissing}>+ {missing.length ? t.select.addAnother : t.select.addMissing}</button>
          {/if}
        </div>
        {#if notice}<p class="notice">{notice}</p>{/if}
        {#if draftState && !preview}<p class="draft_state">✓ {invite ? t.draftSavedEverywhere : t.draftSaved}</p>{/if}
        <footer>
          <Button variant="secondary" size="sm" text={t.back} on:click={() => show("intro")} />
          <div class="submit_group">
            <em>{t.select.changed(changedCount)}</em>
            <Button variant="secondary" size="sm" text={saving ? t.collect.submitting : t.select.submit} disabled={saving} on:click={submitVotes} />
          </div>
        </footer>
      </section>
    {:else}
      <section class="card state_card sent">
        <span class="check" aria-hidden="true"></span>
        <h2>{t.select.sentTitle}</h2>
        <p>{sentElsewhere ? t.alreadySent : t.select.sentText(deadline)}</p>
        {#if !sentElsewhere}<Button variant="secondary" size="sm" text={t.select.sentEdit} on:click={() => show("main")} />{/if}
      </section>
    {/if}
  {:else}
    <section class="card">
      <h1>{survey.mode === "collect" ? t.done.titleCollect : t.done.title}</h1>
      <p class="lead">{survey.mode === "collect" ? t.done.leadCollect : t.done.lead}</p>
      {#each themes as theme}
        {#if theme.tasks.some((task) => task.selected)}
          <h2 class="theme">{theme.name}</h2>
          <ol class="task_list">
            {#each theme.tasks.filter((task) => task.selected) as task}
              <li class="task_row">
                <span class="number done_mark" aria-hidden="true"></span>
                <div><strong>{task.name}</strong>{#if task.description}<p>{task.description}</p>{/if}</div>
                {#if survey.mode !== "collect"}<div class="facts"><span>{t.done.votes(task.yes, task.no)}</span></div>{/if}
              </li>
            {/each}
          </ol>
        {/if}
      {/each}
      {#if allTasks.some((task) => !task.selected)}
        <details class="not_selected">
          <summary>{t.done.notSelected} ({allTasks.filter((task) => !task.selected).length})</summary>
          <ul>{#each allTasks.filter((task) => !task.selected) as task}<li>{task.name} <span>{t.done.votes(task.yes, task.no)}</span></li>{/each}</ul>
        </details>
      {/if}
    </section>
  {/if}
</main>
{#if removal}
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
  <div class="modal_backdrop" on:click|self={() => (removal = null)}>
    <div class="modal_card" role="dialog" aria-modal="true" aria-labelledby="remove-title">
      <h2 id="remove-title">{t.collect.removeTitle}</h2>
      <p>{t.collect.removeText(removal.name)}</p>
      <div class="modal_actions">
        <Button variant="secondary" size="sm" text={t.collect.cancelEdit} on:click={() => (removal = null)} />
        <Button variant="secondary" size="sm" text={t.collect.remove} on:click={confirmRemove} />
      </div>
    </div>
  </div>
{/if}
</div>
<svelte:window on:keydown={(event) => { if (removal && event.key === "Escape") removal = null; }} />

<style>
  /* Gestaltung (Rueckmeldung 04.10.: "einfach und hochwertig, damit wir Vertrauen schaffen"): eine ruhige Spalte,
     klare Typografie, flaechige Felder statt harter Rahmen, ein eindeutiger Hauptknopf. Hell und dunkel teilen alles
     ausser den Farben; dunkel nimmt die Toene der Plattform (app.css, --pax-*-new) */
  /* Eine Schriftskala fuer die ganze Umfrage (06.10.): Titel, Ueberschrift, Text, klein, Label - Staerken nur 400 und 600 */
  /* body = .875rem wie das globale p der Plattform (dort mit !important), sonst zwei fast gleiche Groessen */
  .survey { --s-fs-title: 2rem; --s-fs-heading: 1.1rem; --s-fs-body: .875rem; --s-fs-small: .8rem; --s-fs-caption: .72rem; line-height: 1.4; }
  .survey :is(h1, h2, h3, h4) { line-height: 1.2; }
  .survey { --s-bg: #fafafb; --s-surface: #ffffff; --s-surface-2: #f3f3f6; --s-line: rgba(28, 28, 32, .09); --s-line-strong: rgba(28, 28, 32, .17);
    --s-text: #1c1c20; --s-text-2: #4a4a55; --s-text-3: #6e6e79; --s-muted: #9a9aa5; --s-placeholder: #a5a5b0;
    --s-select-bg: #1c1c20; --s-select-text: #ffffff; --s-action: #1c1c20; --s-action-text: #ffffff; --s-accent: var(--pax-orange);
    --s-ok-bg: #eaf7ec; --s-ok: #1e7b34; --s-ok-border: #b7e1c0; --s-warn-bg: #fff6f1; --s-warn: #9a3d0a;
    --s-danger-bg: #fef3f2; --s-danger-border: #fda29b; --s-info-bg: #eef4fb; --s-info: #2d5b86;
    --s-notice-bg: #fbfbe8; --s-notice-text: #5d5e12; --s-focus: rgba(28, 28, 32, .08); --s-icon: invert(.5);
    /* Rollen, die die Einzelregeln oben verwenden */
    --s-card: var(--s-surface); --s-input: var(--s-surface); --s-header: var(--s-bg); --s-divider: var(--s-line); --s-border: var(--s-line-strong);
    --s-chip: var(--s-surface-2); --s-soft: var(--s-surface-2); --s-hover: var(--s-surface-2); --s-control: transparent;
    --s-shadow: none; --s-shadow-sm: none;
    min-height: 100vh; background: var(--s-bg); color: var(--s-text); }
  .survey.dark { color-scheme: dark;
    --s-bg: var(--pax-black-new); --s-surface: #232329; --s-surface-2: #2b2b32; --s-line: rgba(255, 255, 255, .08); --s-line-strong: rgba(255, 255, 255, .15);
    --s-text: #f4f5f8; --s-text-2: #babbc5; --s-text-3: #8e8f9a; --s-muted: #70717c; --s-placeholder: #6b6c76;
    --s-select-bg: #f4f5f8; --s-select-text: var(--pax-black-new); --s-action: #f4f5f8; --s-action-text: var(--pax-black-new);
    --s-ok-bg: rgba(142, 240, 149, .1); --s-ok: var(--pax-green-new); --s-ok-border: rgba(142, 240, 149, .3);
    --s-warn-bg: rgba(255, 95, 31, .09); --s-warn: #ffad8a; --s-danger-bg: rgba(240, 142, 144, .1); --s-danger-border: var(--pax-red-new);
    --s-info-bg: rgba(139, 186, 240, .12); --s-info: var(--pax-blue-new);
    --s-notice-bg: rgba(233, 240, 142, .1); --s-notice-text: var(--pax-yellow-new); --s-focus: rgba(255, 255, 255, .1); --s-icon: invert(0) opacity(.75); }

  /* Kopfzeile: Logo, Schritt, Hell/Dunkel - und eine duenne Fortschrittslinie */
  .topbar { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; gap: 1rem; height: 3.75rem; padding: 0 1.75rem;
    border-bottom: var(--bw) solid var(--border-color); background: color-mix(in srgb, var(--s-bg) 88%, transparent); backdrop-filter: blur(12px); }
  .topbar_logo { display: flex; align-items: center; width: 4.75rem; height: 1.5rem; }
  /* Logo-Bild ist inline und saesse sonst auf der Grundlinie statt mittig */
  .topbar_logo > :global(div) { display: flex; align-items: center; }
  .topbar_logo :global(img) { display: block; }
  .topbar_step { margin-left: auto; color: var(--s-text-3); font-size: var(--s-fs-small); letter-spacing: .01em; }
  /* Rand wie die Kopfleiste der Plattform (Header.svelte); im Dunkelmodus die helle Linie der Umfrage */
  .survey.dark .topbar { border-bottom-color: var(--s-line-strong); }
  .theme_toggle { flex: none; width: 2.25rem; height: 2.25rem; border: 1px solid var(--s-line-strong); border-radius: 50%; background: transparent; cursor: pointer; }
  .topbar_logo + .theme_toggle { margin-left: auto; } /* ohne Schrittanzeige (Umfrage abgeschlossen) trotzdem rechts */
  .theme_toggle::before { content: ""; display: block; width: 1.05rem; height: 1.05rem; margin: auto; background: var(--s-text-2);
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z'/%3E%3C/svg%3E") center / contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z'/%3E%3C/svg%3E") center / contain no-repeat; }
  .theme_toggle.dark::before { -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round'%3E%3Ccircle cx='12' cy='12' r='4'/%3E%3Cpath d='M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4'/%3E%3C/svg%3E"); mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round'%3E%3Ccircle cx='12' cy='12' r='4'/%3E%3Cpath d='M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4'/%3E%3C/svg%3E"); }
  .theme_toggle:hover { background: var(--s-surface-2); }

  /* Spalte und Typografie */
  .survey .page { min-height: calc(100vh - 3.75rem); padding: 3.5rem 1.5rem 5rem; overflow-x: clip; background: transparent; color: var(--s-text); } /* clip: Lichtschein der Startseite nicht seitlich scrollbar */
  .survey .card { width: min(46rem, 100%); padding: 0; border: 0; border-radius: 0; background: transparent; box-shadow: none; }
  .survey h1 { margin-bottom: 1rem; font-size: var(--s-fs-title); font-weight: 600; line-height: 1.2; letter-spacing: -.02em; text-align: left; }
  .survey h2, .survey h3, .survey .section_title, .survey .list_title { font-weight: 600; letter-spacing: -.01em; }
  .survey .lead { color: var(--s-text-2); font-size: var(--s-fs-heading); text-align: left; }
  .survey .card p, .survey .card .lead { line-height: 1.4; }
  .survey .title_row { flex-direction: row; align-items: flex-start; text-align: left; }
  .survey .state_card h2 { font-weight: 600; }
  /* Folgeseiten (Rueckmeldung 04.10.): gleiche Qualitaet wie die Startseite - grosse Ueberschriften, Karten mit
     leichtem Schatten, Antworten als waehlbare Karten, klare Symbole */
  .survey { --s-elevated: 0 1px 2px rgba(16, 24, 40, .04), 0 18px 44px -18px rgba(16, 24, 40, .18); }
  .survey.dark { --s-elevated: 0 18px 44px -18px rgba(0, 0, 0, .6); }
  .survey .intro > .lead, .survey .title_row .lead { max-width: 38rem; }
  /* Pax AI: zwei Antworten als Karten */
  .survey .choice { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--control-gap); margin-top: 1rem; }
  .survey .radio.option { position: relative; display: flex; align-items: center; gap: var(--control-gap); min-height: 4.25rem; padding: 1rem 1.15rem;
    border: 1px solid var(--s-line-strong); border-radius: .9rem; background: var(--s-surface); color: var(--s-text); font-size: var(--s-fs-body); font-weight: 400;
    transition: border-color .15s, box-shadow .15s; }
  .survey .radio.option:hover { border-color: var(--s-text-3); }
  .survey .radio.option span { min-width: 0; white-space: normal; overflow-wrap: break-word; } /* lange Antworten brechen um statt aus der Karte zu laufen */
  .survey .radio.option:has(input:checked) { border-color: var(--s-text); box-shadow: 0 0 0 1px var(--s-text), var(--s-elevated); }
  .survey .radio.option input { position: absolute; opacity: 0; pointer-events: none; }
  .survey .radio.option::before { content: ""; flex: none; width: 2.25rem; height: 2.25rem; border-radius: .65rem; background: var(--s-surface-2); }
  .survey .radio.option::after { content: ""; position: absolute; left: calc(1.15rem + .55rem); width: 1.15rem; height: 1.15rem; background: var(--s-text-2);
    -webkit-mask: var(--icon) center / contain no-repeat; mask: var(--icon) center / contain no-repeat; }
  .survey .radio.option:has(input:checked)::before { background: var(--s-accent); }
  .survey .radio.option:has(input:checked)::after { background: #ffffff; }
  .survey .radio.option:has(input:focus-visible) { outline: 2px solid var(--s-text-3); outline-offset: 2px; }
  .survey .radio.spark { --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linejoin='round'%3E%3Cpath d='M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z'/%3E%3Cpath d='M18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z'/%3E%3C/svg%3E"); }
  .survey .radio.pen { --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 20l4.2-1 10.3-10.3a2.1 2.1 0 0 0-3-3L5.2 16 4 20z'/%3E%3Cpath d='M13.5 7.5l3 3'/%3E%3C/svg%3E"); }
  .context_card { margin-top: 1rem; padding: 1.5rem 1.75rem; border: 1px solid var(--s-line); border-radius: 1.1rem; background: var(--s-surface); box-shadow: var(--s-elevated); }
  .survey .context_card .section_title { margin-top: 0; }
  .survey .context_card .intro_fields { width: 100%; }
  /* Aufgaben: Formular als erhobene Karte, Ideen mit Symbol, ruhiger Zaehler */
  .survey .task_form { box-shadow: var(--s-elevated); border-radius: 1.1rem; }
  .survey .ideas_box { border-radius: 1.1rem; }
  .survey .ideas_head strong { display: inline-flex; align-items: center; gap: var(--control-gap); }
  .survey .ideas_head strong::before { content: ""; width: 1.05rem; height: 1.05rem; background: var(--s-accent);
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linejoin='round'%3E%3Cpath d='M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z'/%3E%3Cpath d='M18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z'/%3E%3C/svg%3E") center / contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linejoin='round'%3E%3Cpath d='M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z'/%3E%3Cpath d='M18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z'/%3E%3C/svg%3E") center / contain no-repeat; }
  .notice_card { display: flex; align-items: flex-start; gap: var(--control-gap); max-width: 32rem; padding: 1.15rem 1.35rem; border: 1px solid var(--s-line);
    border-radius: 1rem; background: var(--s-surface); box-shadow: var(--s-elevated); text-align: left; }
  .notice_icon { display: grid; flex: none; place-items: center; width: 1.6rem; height: 1.6rem; border-radius: 50%; background: var(--s-surface-2);
    color: var(--s-text-2); font-size: var(--s-fs-body); font-weight: 600; font-style: italic; }
  .survey .notice_card p { margin: .1rem 0 0; color: var(--s-text); font-size: var(--s-fs-body); }
  .icon_btn svg.trash { width: 15px; height: 15px; color: var(--s-text-3); filter: none; }
  .icon_btn.danger:hover svg.trash { color: #c0392b; }
  .regen_loading { padding: .45rem .2rem; }
  .modal_backdrop { position: fixed; inset: 0; z-index: 50; display: grid; place-items: center; padding: 1rem; background: rgba(16, 16, 20, .45);
    backdrop-filter: blur(3px); }
  .modal_card { width: min(26rem, 100%); box-sizing: border-box; padding: 1.5rem 1.6rem 1.25rem; border: 1px solid var(--s-line); border-radius: 1.1rem;
    background: var(--s-surface); color: var(--s-text); box-shadow: 0 24px 60px -20px rgba(0, 0, 0, .45); }
  .modal_card h2 { margin: 0 0 .4rem; font-size: var(--s-fs-heading); font-weight: 600; }
  .survey .modal_card p { margin: 0; color: var(--s-text-2); font-size: var(--s-fs-body); line-height: 1.4; overflow-wrap: anywhere; }
  .modal_actions { display: flex; justify-content: flex-end; gap: var(--control-gap); margin-top: 1rem; }
  .regen { display: inline-flex; align-items: center; gap: .35rem; padding: .45rem .9rem; border: 1px solid var(--s-line-strong); border-radius: 999px;
    background: transparent; color: var(--s-text); font: inherit; font-size: var(--s-fs-small); font-weight: 400; cursor: pointer; }
  .regen:hover { background: var(--s-surface-2); }
  .survey .collected { padding: .45rem .9rem; border-radius: 999px; font-size: var(--s-fs-small); font-weight: 600; }
  .survey .list_title { margin-top: 1rem; font-size: var(--s-fs-heading); }
  .survey .task_row { padding: 1.05rem 1.2rem; }
  /* Pruefen: Zeitraum als klare Karte mit Kalender */
  .survey .period_callout { position: relative; padding: 1.15rem 1.35rem 1.15rem 3.6rem; border: 1px solid var(--s-line); border-left: 1px solid var(--s-line);
    border-radius: 1rem; background: var(--s-surface); box-shadow: var(--s-elevated); }
  .survey .period_callout::before { content: ""; position: absolute; top: 1.15rem; left: 1.2rem; width: 1.5rem; height: 1.5rem; background: var(--s-accent);
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round'%3E%3Crect x='3.5' y='5' width='17' height='15' rx='2.5'/%3E%3Cpath d='M3.5 10h17M8 3v4M16 3v4'/%3E%3C/svg%3E") center / contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round'%3E%3Crect x='3.5' y='5' width='17' height='15' rx='2.5'/%3E%3Cpath d='M3.5 10h17M8 3v4M16 3v4'/%3E%3C/svg%3E") center / contain no-repeat; }
  .survey .period_callout strong { font-size: var(--s-fs-heading); }
  .survey .theme { margin-top: 1rem; color: var(--s-text-2); font-size: var(--s-fs-small); font-weight: 600; letter-spacing: .06em; text-transform: uppercase; }
  .survey h3.theme { margin-top: 1rem; }
  /* Bereiche "L1: L2": Leiste auf der Eingabeseite, Kasten je Bereich auf der Pruefseite (07.10.) */
  .area_intro { margin: 0 0 .6rem; color: var(--s-text-2); font-size: var(--s-fs-small); }
  .area_steps { display: flex; flex-wrap: wrap; gap: .5rem; margin: 0 0 1.75rem; }
  .area_step.done .area_number { background: var(--s-ok-bg); color: var(--s-ok); }
  .area_parent { display: block; margin-bottom: .2rem; color: var(--s-text-3); font-size: var(--s-fs-small); font-weight: 600; letter-spacing: .06em;
    text-transform: uppercase; }
  .area_step { display: inline-flex; align-items: center; gap: .45rem; padding: .35rem .8rem .35rem .4rem; border: 1px solid var(--s-line);
    border-radius: 999px; background: var(--s-surface); color: var(--s-text-2); font: inherit; font-size: var(--s-fs-small); cursor: pointer; }
  .area_step:hover { border-color: var(--s-line-strong); }
  .area_step.current { border-color: var(--s-text); color: var(--s-text); font-weight: 600; cursor: default; }
  .area_number { display: inline-grid; place-items: center; width: 1.4rem; height: 1.4rem; border-radius: 50%; background: var(--s-surface-2);
    color: var(--s-text-2); font-size: var(--s-fs-caption); font-weight: 600; }
  .area_step.current .area_number { background: var(--s-accent); color: #fff; }
  .area_step b { color: var(--s-text-3); font-weight: 600; }
  .survey .area_title { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin: 1.75rem 0 .25rem; padding: .8rem 1.1rem;
    border: 1px solid var(--s-line); border-left: 3px solid var(--s-accent); border-radius: .8rem; background: var(--s-surface);
    color: var(--s-text); font-size: var(--s-fs-heading); font-weight: 600; }
  .survey .area_title b { color: var(--s-text-3); font-size: var(--s-fs-small); font-weight: 600; }
  /* Danke-Seite: grosses Haekchen mit weichem Ring */
  .survey .state_card { gap: 1rem; margin-top: 4rem; }
  /* Plattform-Buttons (secondary = --pax-black); im Dunkelmodus mit Rand, sonst verschwinden sie auf der dunklen Flaeche */
  .survey.dark :global(.button-secondary) { border: 1px solid var(--s-line-strong); }
  /* Danke-Seite steht allein: senkrecht mittig unter der Kopfleiste (3.75rem) und dem Seitenabstand (4.5rem + 3rem) */
  .survey .state_card.sent { min-height: calc(100vh - 11.25rem); align-content: center; margin-top: 0; }
  .survey .sent .check { width: 4rem; height: 4rem; background-size: 1.9rem; box-shadow: 0 0 0 10px var(--s-ok-bg); margin-bottom: 0; }
  .survey .state_card h2 { font-size: var(--s-fs-title); letter-spacing: -.02em; }
  .survey .state_card p { max-width: 30rem; color: var(--s-text-2); }
  /* Startseite: Lichtschein, Teammarke, Zeitleiste, Karte fuer Schritt 1, Zusagen */
  .hero { position: relative; isolation: isolate; }
  .hero::before { content: ""; position: absolute; z-index: -1; top: -5rem; left: -12rem; width: 38rem; height: 24rem; pointer-events: none;
    background: radial-gradient(closest-side, rgba(255, 95, 31, .11), transparent); }
  .survey.dark .hero::before { background: radial-gradient(closest-side, rgba(255, 95, 31, .14), transparent); }
  .eyebrow { display: inline-flex; margin-bottom: 1rem; padding: .3rem .8rem; border: 1px solid var(--s-line-strong); border-radius: 999px;
    background: var(--s-surface); color: var(--s-text-2); font-size: var(--s-fs-caption); font-weight: 400; }
  .survey .hero h1 { max-width: 42rem; font-size: var(--s-fs-title); line-height: 1.2; letter-spacing: -.025em; }
  .survey .hero .lead { max-width: 38rem; font-size: var(--s-fs-heading); }
  .tl_when { color: var(--s-muted); font-size: var(--s-fs-caption); font-weight: 600; letter-spacing: .07em; text-transform: uppercase; }
  .tl_when.now { color: var(--s-accent); }
  .today_card { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 1rem; margin-top: 1rem; padding: 1.5rem 1.75rem;
    border: 1px solid var(--s-line); border-radius: 1.1rem; background: var(--s-surface);
    box-shadow: 0 1px 2px rgba(16, 24, 40, .04), 0 18px 44px -18px rgba(16, 24, 40, .18); }
  .survey.dark .today_card { box-shadow: 0 18px 44px -18px rgba(0, 0, 0, .6); }
  .today_art { display: grid; place-items: center; width: 7rem; height: 5.5rem; border-radius: .9rem; background: var(--s-surface-2); }
  .today_art svg { width: 6rem; height: 4rem; }
  .today_body h2 { margin: .25rem 0 .3rem; font-size: var(--s-fs-heading); font-weight: 600; letter-spacing: -.01em; }
  .survey .today_body p { margin: 0; color: var(--s-text-2); font-size: var(--s-fs-body); }
  .survey .today_body .meta { margin-top: var(--control-gap); gap: var(--control-gap); font-size: var(--s-fs-small); }
  .trust { display: flex; flex-wrap: wrap; gap: var(--control-gap); margin: 1rem 0 0; padding: 0; color: var(--s-text-3); font-size: var(--s-fs-body); list-style: none; }
  .meta_item.users { --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round'%3E%3Ccircle cx='9' cy='8.5' r='3.2'/%3E%3Cpath d='M3 19c.6-3.3 3-5.2 6-5.2s5.4 1.9 6 5.2'/%3E%3Cpath d='M15.5 5.6a3.2 3.2 0 0 1 0 5.8M17.5 13.9c1.9.6 3.1 2.4 3.5 5.1'/%3E%3C/svg%3E"); }
  .meta_item.together { --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 20s-7-4.3-7-9.6A4 4 0 0 1 12 8a4 4 0 0 1 7 2.4C19 15.7 12 20 12 20z'/%3E%3C/svg%3E"); }
  .ill-card { fill: var(--s-bg); stroke: var(--s-line-strong); stroke-width: 1.5; }
  .ill-box { fill: none; stroke: var(--s-text-3); stroke-width: 1.6; }
  .ill-line { fill: none; stroke: var(--s-text-3); stroke-width: 2.6; stroke-linecap: round; }
  .ill-person { fill: var(--s-surface-2); stroke: var(--s-text-3); stroke-width: 1.6; }
  .ill-accent { fill: var(--s-accent); }
  .ill-accent-line { fill: var(--s-surface); stroke: var(--s-accent); stroke-width: 2.4; stroke-linejoin: round; }
  .ill-white { fill: none; stroke: #ffffff; stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
  .ill-white-fill { fill: #ffffff; }
  .ill-ring { fill: var(--s-surface); stroke: var(--s-line-strong); stroke-width: 1.6; }
  .ill-stop { fill: var(--s-text-3); }
  .ill-axis { fill: none; stroke: var(--s-line-strong); stroke-width: 1.6; stroke-linecap: round; }
  .ill-bar { fill: var(--s-surface-2); stroke: var(--s-text-3); stroke-width: 1.6; }
  .ill-accent-stroke { fill: none; stroke: var(--s-accent); stroke-width: 2.6; stroke-linecap: round; stroke-linejoin: round; }
  /* Vertrauenszeile */
  .survey .meta { justify-content: flex-start; gap: var(--control-gap); margin-top: 1rem; color: var(--s-text-3); font-size: var(--s-fs-body); }
  .meta_item { display: inline-flex; align-items: center; gap: var(--control-gap); }
  .meta_item::before { content: ""; flex: none; width: 1.05rem; height: 1.05rem; background: currentColor;
    -webkit-mask: var(--icon) center / contain no-repeat; mask: var(--icon) center / contain no-repeat; }
  .meta_item.time { --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round'%3E%3Ccircle cx='12' cy='12' r='9'/%3E%3Cpath d='M12 7v5l3 2'/%3E%3C/svg%3E"); }
  .meta_item.date { --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round'%3E%3Crect x='3.5' y='5' width='17' height='15' rx='2.5'/%3E%3Cpath d='M3.5 10h17M8 3v4M16 3v4'/%3E%3C/svg%3E"); }
  .meta_item.lock { --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round'%3E%3Crect x='5' y='10.5' width='14' height='10' rx='2.5'/%3E%3Cpath d='M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3'/%3E%3C/svg%3E"); }
  /* Flaechen statt Rahmen */
  .survey .example, .survey .model_task, .survey .area_panel, .survey .ideas_box, .survey .task_form {
    border: 1px solid var(--s-line); border-radius: .9rem; background: var(--s-surface); box-shadow: none; }
  .survey .example, .survey .model_task { border-left-width: 1px; }
  .survey .task_form { padding: 1.75rem; }
  .survey .task_row { border: 1px solid var(--s-line); border-radius: .8rem; background: var(--s-surface); }
  .survey .task_row:hover { border-color: var(--s-line-strong); }
  .survey .number { background: var(--s-surface-2); color: var(--s-text-2); }
  .survey .period_callout { border-left: 3px solid var(--s-accent); border-radius: .8rem; background: var(--s-warn-bg); }
  .survey .period_question { background: transparent; padding: 0; }
  .survey .period_question > span { color: var(--s-text-3); font-weight: 400; }
  .survey .collected { border: 1px solid var(--s-line); background: var(--s-surface); color: var(--s-text-2); }
  .survey footer { margin-top: 1rem; border-top: 1px solid var(--s-line); }
  /* Eingaben */
  .survey input, .survey textarea { border: 1px solid var(--s-line-strong); border-radius: .65rem; background: var(--s-surface); color: var(--s-text); box-shadow: none; }
  .survey .task_form input, .survey .task_form textarea { background: var(--s-bg); } /* im Kasten eine Stufe abgesetzt */
  .survey input:focus, .survey textarea:focus { border-color: var(--s-text-3); box-shadow: 0 0 0 3px var(--s-focus); }
  /* Chips und Ja/Nein: zurueckhaltend, ausgewaehlt als volle Flaeche */
  .survey .chip { border: 1px solid var(--s-line-strong); background: transparent; color: var(--s-text); }
  .survey .chip:hover { background: var(--s-surface-2); }
  .survey .chip.selected { border-color: var(--s-select-bg); background: var(--s-select-bg); color: var(--s-select-text); }
  .survey .toggle { border: 1px solid var(--s-line-strong); border-radius: .6rem; }
  .survey .toggle button { background: transparent; color: var(--s-text); font-weight: 400; }
  .survey .toggle button + button { border-left-color: var(--s-line-strong); }
  .survey .toggle button.selected { background: var(--s-select-bg); color: var(--s-select-text); }
  /* Knoepfe: eine klare Hauptaktion, Nebenaktionen ruhig */
  .page { min-height: 100vh; padding: 4.5rem 2rem 3rem; box-sizing: border-box; background: var(--s-bg); color: var(--s-text); }
  /* zoom 1.07: die Plattform rechnet mit 14px Grundschrift (schmal 12px) - fuer die Umfrage etwas zu klein; 1.15 war
     zu gross (Rueckmeldung 02.10.). Gestoert hat vor allem der enge Zeilenabstand, siehe unten */
  .card { width: min(64rem, 100%); box-sizing: border-box; margin: 0 auto; padding: 2.5rem; border: 1px solid var(--s-divider); border-radius: 1rem; background: var(--s-card);
    box-shadow: var(--s-shadow); zoom: 1.07; }
  .state { min-height: 60vh; display: grid; place-items: center; }
  .state_card { display: grid; justify-items: center; gap: 1rem; margin-top: 3rem; text-align: center; }
  .state_card h2 { margin: 0; font-size: var(--s-fs-heading); }
  .state_card p { margin: 0; color: var(--s-text-3); }
  h1 { margin: 0 0 1rem; font-size: var(--s-fs-title); font-weight: 600; line-height: 1.2; letter-spacing: -.01em; }
  h2 { font-weight: 600; }
  .lead { margin: 0 0 .5rem; color: var(--s-text-2); font-size: var(--s-fs-body); line-height: 1.4; }
  .meta { display: flex; flex-wrap: wrap; align-items: center; gap: var(--control-gap); margin: 1rem 0 0; color: var(--s-text-2); font-size: var(--s-fs-body); }
  .example { margin-top: 1rem; padding: 1.25rem; border-radius: .6rem; background: var(--s-soft); }
  .example_label { margin: 0 0 var(--control-gap); color: var(--s-text-3); font-size: var(--s-fs-caption); font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
  .hint { margin: var(--control-gap) 0 0; color: var(--s-text-3); font-size: var(--s-fs-body); }
  .criteria { display: flex; flex-wrap: wrap; gap: var(--control-gap); margin: 0; padding: 0; list-style: none; }
  .criteria li { padding: .35rem .75rem; border-radius: 1rem; background: var(--s-ok-bg); color: var(--s-ok); font-size: var(--s-fs-body); font-weight: 600; }
  .criteria li::before { content: "✓ "; }
  footer { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 1rem; padding-top: 1.5rem; border-top: 1px solid var(--s-divider); }
  .title_row { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between; gap: 1rem; }
  .title_row > div:first-child { flex: 1 1 18rem; min-width: 0; }
  .collected { padding: .4rem .8rem; border-radius: 2rem; background: var(--s-chip); font-size: var(--s-fs-body); white-space: nowrap; }
  .ideas_box { margin-top: 1rem; padding: 1rem 1.1rem; border: 1px solid var(--s-divider); border-radius: .9rem; }
  .ideas_head { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
  .ideas_head strong { font-size: var(--s-fs-body); }
  .ideas_hint { margin: var(--control-gap) 0; color: var(--s-text-3); font-size: var(--s-fs-small); }
  .idea_list { margin-top: var(--control-gap); padding-top: var(--control-gap); border-top: 1px solid var(--s-chip); }
  .ideas_more { margin-top: var(--control-gap); }
  .chip.suggestion:disabled { border-style: solid; background: var(--s-ok-bg); color: var(--s-ok); cursor: default; }
  .section_title { margin: 1rem 0 var(--control-gap); font-size: var(--s-fs-heading); }
  .section_title.first { margin-top: 0; }
  .intro_fields { width: min(40rem, 100%); }
  .choice { display: grid; gap: var(--control-gap); margin-top: var(--control-gap); }
  .radio { display: flex; align-items: center; gap: var(--control-gap); font-size: var(--s-fs-body); cursor: pointer; }
  .radio input { width: auto; margin: 0; }
  .question_hint { display: block; margin: -.25rem 0 .5rem; }
  .form_actions .coach { color: var(--s-warn); font-style: normal; }
  .review_more { margin-top: 1rem; }
  .period_callout { display: grid; gap: var(--control-gap); margin: 0 0 1rem; padding: 1rem 1.25rem; border-left: .25rem solid var(--pax-orange); border-radius: .6rem; background: var(--s-warn-bg); }
  .period_callout strong { font-size: var(--s-fs-heading); }
  .period_callout p { margin: 0; color: var(--s-text-2); font-size: var(--s-fs-body); }
  .period_callout b { color: var(--s-text); }
  .period_question.open { outline: 2px solid var(--pax-orange); outline-offset: 2px; }
  .period_open { margin: 1rem 0 0; padding: .75rem 1rem; border-radius: .5rem; background: var(--s-warn-bg); color: var(--s-warn); font-size: var(--s-fs-body); }
  .review_row { grid-template-columns: auto 1fr auto auto; } /* ohne Angaben: Nummer, Aufgabe, Icons, Zeitraum */
  .period_question { display: grid; justify-items: start; gap: var(--control-gap); padding: .5rem .75rem; border-radius: .5rem; background: var(--s-warn-bg); }
  /* Bearbeiten/Entfernen wie in der Plattform (ProcessOverview, DataTableBlack): schwarzes Feld, weisses Symbol */
  .icon_btn { display: grid; place-items: center; width: 1.75rem; height: 1.75rem; padding: 0; border: 1px solid var(--s-border); border-radius: .4rem; background: var(--s-card);
    cursor: pointer; transition: background .15s, border-color .15s; }
  /* die Plattform-Symbole sind weiss (fuer schwarze Felder) - hier dunkelgrau eingefaerbt */
  .icon_btn :global(svg) { width: 13px; height: 13px; filter: var(--s-icon); }
  .icon_btn:hover { border-color: var(--s-muted); background: var(--s-hover); }
  .icon_btn.danger:hover { border-color: var(--s-danger-border); background: var(--s-danger-bg); }
  .period_question > span { color: var(--s-warn); font-size: var(--s-fs-small); font-weight: 600; }
  .period_question .toggle { width: 9rem; }
  .period_question .toggle button { padding: .35rem; font-size: var(--s-fs-small); }
  h3.theme { margin: 1rem 0 var(--control-gap); font-size: var(--s-fs-body); }
  .review_more p { margin: 0 0 var(--control-gap); color: var(--s-text-2); font-size: var(--s-fs-body); }
  textarea { resize: vertical; }
  .field_hint.coach { color: var(--s-warn); }
  /* ruhig statt Signalfarbe: das Beispiel soll da sein, aber nicht die Seite dominieren (Rueckmeldung 02.10.) */
  .model_task { display: grid; gap: var(--control-gap); margin-top: 1rem; padding: 1rem 1.25rem; border-left: .2rem solid var(--s-border); border-radius: .4rem; background: var(--s-soft); }
  .model_task > span { color: var(--s-text-3); font-size: var(--s-fs-caption); font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
  /* Label oben abgesetzt, Name und Ablauf als ein Block darunter */
  .model_text { display: grid; gap: .2rem; }
  .model_task strong { font-size: var(--s-fs-body); }
  .model_task em { color: var(--s-text-2); font-size: var(--s-fs-body); font-style: normal; }
  .text_fields.single { grid-template-columns: 1fr; }
  .assist_loading { display: inline-flex; align-items: center; gap: var(--control-gap); color: var(--s-text-2); font-size: var(--s-fs-body); }
  .chip.area.done { border-color: var(--s-ok-border); background: var(--s-ok-bg); color: var(--s-ok); }
  .area_panel { display: flex; flex-wrap: wrap; align-items: center; gap: var(--control-gap); margin-top: var(--control-gap); padding: .75rem .9rem; border-radius: .6rem; background: var(--s-soft); }
  .area_panel > span { width: 100%; color: var(--s-text-3); font-size: var(--s-fs-small); }
  .suggestions { margin-top: 1rem; }
  .suggestions p { margin: 0 0 var(--control-gap); color: var(--s-text-2); font-size: var(--s-fs-body); }
  .chips { display: flex; flex-wrap: wrap; gap: var(--control-gap); }
  .chip { padding: .5rem .9rem; border: 1px solid var(--s-border); border-radius: 2rem; background: var(--s-card); color: var(--s-text); font-size: var(--s-fs-small); font-weight: 400; cursor: pointer;
    transition: background .15s, border-color .15s; }
  .chip:hover { border-color: var(--s-muted); background: var(--s-hover); }
  .chip.selected { border-color: var(--s-select-bg); background: var(--s-select-bg); color: var(--s-select-text); }
  .chip.unknown { color: var(--s-text-3); font-weight: 400; }
  .chip.unknown.selected { color: var(--s-select-text); }
  .chip.suggestion { border-style: dashed; }
  .chip b { margin-left: .3rem; font-weight: 600; opacity: .7; }
  .task_form { display: grid; gap: 1rem; margin-top: 1rem; padding: 1.25rem; border: 1px solid var(--s-divider); border-radius: .9rem; background: var(--s-card);
    box-shadow: var(--s-shadow-sm); scroll-margin: 6rem; }
  .task_form.editing { border-color: var(--pax-orange); }
  .text_fields { display: grid; grid-template-columns: 1fr 1.3fr; align-items: start; gap: var(--control-gap); }
  label { display: grid; align-content: start; gap: var(--control-gap); }
  label > span, .question > span, .decision > span { color: var(--s-text); font-size: var(--s-fs-small); font-weight: 600; line-height: 1.2; }
  label em, .missing em, .question em.optional { color: var(--s-muted); font-weight: 400; font-style: normal; }
  .question em.optional { display: block; padding-left: 1.1rem; font-size: var(--s-fs-small); } /* unter der Frage, buendig nach dem Kreis */
  .field_hint { color: var(--s-text-3); font-size: var(--s-fs-caption); line-height: 1.4; }
  input, textarea { box-sizing: border-box; width: 100%; padding: .8rem 1rem; border: 1px solid var(--s-border); border-radius: .6rem; background: var(--s-input); color: var(--s-text);
    font: inherit; font-size: var(--s-fs-body); line-height: 1.4; box-shadow: var(--s-shadow-sm); transition: border-color .15s, box-shadow .15s; }
  /* etwas heller als der Standard - Beispiele sollen nicht wie schon ausgefuellt wirken */
  input::placeholder, textarea::placeholder { color: var(--s-placeholder); font-size: var(--s-fs-body); } /* Beispiele etwas kleiner als Eingaben */
  input:focus, textarea:focus { border-color: var(--s-muted); outline: none; box-shadow: 0 0 0 4px var(--s-focus); }
  .questions { display: grid; border-top: 1px solid var(--s-divider); }
  .questions_head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: var(--control-gap); padding: .9rem 0 .35rem; }
  .questions_head strong { font-size: var(--s-fs-body); }
  .questions_head em { margin-left: .4rem; color: var(--s-text-3); font-size: var(--s-fs-small); font-style: normal; font-weight: 400; }
  /* Je Frage eine Zeile: Frage links, Antworten rechts - alles auf derselben Kante (wie im Potential Scout) */
  .question { display: grid; grid-template-columns: 15rem minmax(0, 1fr); align-items: center; gap: var(--control-gap); padding: .55rem 0; border-bottom: 1px solid var(--s-divider); }
  .question > span { white-space: nowrap; } /* jede Frage in einer Zeile, die Antworten rechts daneben */
  .question:last-child { border-bottom: 0; }
  .question > span::before { content: "○"; display: inline-block; width: 1.1rem; color: var(--s-muted); }
  .question.answered > span::before { content: "✓"; color: var(--s-ok); }
  .form_actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: var(--control-gap); }
  .form_actions em, .submit_group em { color: var(--s-text-3); font-size: var(--s-fs-small); font-style: normal; }
  .list_title { margin: 1rem 0 var(--control-gap); font-size: var(--s-fs-heading); }
  .task_list { display: grid; gap: var(--control-gap); margin: 0; padding: 0; list-style: none; }
  .task_row { display: grid; grid-template-columns: auto minmax(14rem, 1fr) auto auto; align-items: center; gap: var(--control-gap); padding: 1rem 1.1rem; border: 1px solid var(--s-divider); border-radius: .75rem;
    background: var(--s-card); transition: border-color .15s; }
  .task_row:hover { border-color: var(--s-border); }
  .task_row.editing { border-color: var(--pax-orange); }
  .task_row strong { font-size: var(--s-fs-body); }
  .task_row p { margin: .2rem 0 0; color: var(--s-text-3); font-size: var(--s-fs-small); line-height: 1.4; }
  .number { display: grid; place-items: center; width: 1.9rem; height: 1.9rem; border-radius: 50%; background: var(--s-chip); color: var(--s-text-2); font-size: var(--s-fs-small); font-weight: 600; }
  .facts { display: flex; flex-wrap: wrap; gap: var(--control-gap); }
  .facts span { padding: .2rem .55rem; border-radius: 1rem; background: var(--s-chip); color: var(--s-text-2); font-size: var(--s-fs-caption); white-space: nowrap; }
  .row_actions { display: flex; gap: var(--control-gap); }
  .link { padding: 0; border: 0; background: none; color: var(--s-text-2); font-size: var(--s-fs-small); text-decoration: underline; cursor: pointer; }
  .empty { margin: 0; color: var(--s-text-3); }
  .draft_state { margin: 1rem 0 0; color: var(--s-ok); font-size: var(--s-fs-small); }
  .notice { margin: 1rem 0 0; padding: .75rem 1rem; border-radius: .5rem; background: var(--s-notice-bg); color: var(--s-notice-text); font-size: var(--s-fs-body); }
  .submit_group { display: flex; flex: 1 1 16rem; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: var(--control-gap); min-width: 0; }
  .filters { margin: 1rem 0 var(--control-gap); }
  .theme { display: flex; align-items: center; gap: var(--control-gap); margin: 1rem 0 var(--control-gap); font-size: var(--s-fs-heading); }
  .theme span { color: var(--s-muted); font-size: var(--s-fs-body); font-weight: 400; }
  .vote_row { grid-template-columns: minmax(0, 1.6fr) minmax(10rem, .8fr) minmax(11rem, .8fr); align-items: start; }
  .task_text .facts { margin-top: var(--control-gap); }
  .badge { margin-left: .35rem; padding: .15rem .5rem; border-radius: 1rem; background: var(--s-info-bg); color: var(--s-info); font-size: var(--s-fs-caption); font-weight: 600; white-space: nowrap; }
  .assessment { display: grid; gap: .2rem; padding: .6rem .75rem; border-radius: .5rem; font-size: var(--s-fs-small); }
  .assessment span { color: var(--s-text-2); }
  .assessment.suitable { background: var(--s-ok-bg); }
  .assessment.suitable strong { color: var(--s-ok); }
  .assessment.not_suitable { background: var(--s-warn-bg); }
  .assessment.not_suitable strong { color: var(--s-warn); }
  .assessment.unclear, .assessment.framework { background: var(--s-chip); }
  .decision { display: grid; gap: var(--control-gap); }
  .toggle { display: grid; grid-template-columns: 1fr 1fr; overflow: hidden; border: 1px solid var(--s-border); border-radius: .5rem; }
  .toggle button { padding: .55rem; border: 0; background: var(--s-card); color: var(--s-text); font-weight: 600; cursor: pointer; }
  .toggle button + button { border-left: 1px solid var(--s-border); }
  .toggle button.selected { background: var(--s-select-bg); color: var(--s-select-text); }
  .decision .link { justify-self: start; }
  .missing { margin-top: 1rem; }
  .missing .list_title { margin-top: 0; }
  .missing .task_list { margin-bottom: 1rem; }
  .missing_row { grid-template-columns: 1fr auto auto; }
  .sent .check { width: 3rem; height: 3rem; border-radius: 50%; background: var(--pax-green) url("/img/assistant/icons/check_dark.svg") center / 1.5rem no-repeat; }
  .done_mark { background: var(--pax-green) url("/img/assistant/icons/check_dark.svg") center / 1rem no-repeat; }
  .not_selected { margin-top: 1rem; color: var(--s-text-3); }
  .not_selected summary { cursor: pointer; font-weight: 600; }
  .not_selected li span { margin-left: .5rem; font-size: var(--s-fs-small); }
  /* Luft zwischen den Zeilen fuer Fliesstext und Hinweise - steht hinter den Einzelregeln, damit es sie ueberschreibt */
  .card p, .card .field_hint, .card .model_task em { line-height: 1.4; }
  /* Unter 1024px setzt die Plattform 12px Grundschrift (app.css) - auf Tablet und Handy war die Umfrage kaum lesbar
     (Rueckmeldung 02.10. vom Handy); 1.25 bzw. 1.33 ergeben etwa 15px bzw. 16px Fliesstext */
  @media (max-width: 1024px) {
    .card { zoom: 1.25; }
  }
  @media (max-width: 800px) {
    .survey { --s-fs-title: 1.6rem; }
    /* Handy (Rueckmeldung 04.10.): Hinweis unter die Ueberschrift, nichts abgeschnitten, Knoepfe gleich breit */
    .questions_head em { display: block; margin: .2rem 0 0; }
    .form_actions { justify-content: stretch; }
    .form_actions .coach { flex: 1 0 100%; text-align: left; }
    .today_action :global(button) { width: 100% !important; }
    /* eine Zeile: Zurueck schmal, Hauptaktion fuellt den Rest */
    .form_actions { flex-wrap: wrap; }
    .modal_card { zoom: 1.25; }
    .survey .choice { grid-template-columns: 1fr; }
    .context_card { padding: 1.25rem; }
    /* Handy: Karte untereinander, Knopf ueber die ganze Breite */
    .today_card { grid-template-columns: 1fr; gap: 1rem; padding: 1.25rem; }
    .today_art { width: 100%; height: 5rem; }
    .today_action :global(button) { width: 100%; }
    .survey .hero h1 { font-size: var(--s-fs-title); }
    /* die eine Aufforderung frueh sichtbar: Karte direkt unter dem Einleitungssatz */
    .hero { display: flex; flex-direction: column; }
    .hero .eyebrow { align-self: flex-start; }
    .today_card { order: 1; margin-top: 1rem; }
    .trust { order: 3; }
    .page { padding: 4rem .75rem 2rem; }
    .card { padding: 1.25rem; zoom: 1.33; }
    /* global gilt p { font-size: .875rem !important } - der Fliesstext braucht daher ebenfalls !important, um auf
       dem Handy ~16px zu erreichen; Eingaben mit 16px verhindern das automatische Hineinzoomen auf dem iPhone */
    .lead { font-size: 1rem !important; }
    .field_hint, .hint, .ideas_hint { font-size: var(--s-fs-body); }
    input, textarea { font-size: 1rem; }
    h1 { font-size: var(--s-fs-heading); }
    .text_fields { grid-template-columns: 1fr; }
    .question { grid-template-columns: 1fr; gap: var(--control-gap); }
    .question > span { white-space: normal; }
    .task_row { grid-template-columns: 1fr; gap: var(--control-gap); }
    .vote_row { grid-template-columns: 1fr 1fr; gap: var(--control-gap); }
    .vote_row .task_text { grid-column: 1 / -1; }
    .number { display: none; }
    /* Pruefseite (Rueckmeldung 04.10.): Symbole links und Ja/Nein rechts in einer Zeile */
    .review_row { grid-template-columns: auto minmax(0, 1fr); align-items: center; row-gap: var(--control-gap); }
    .review_row > div:not(.row_actions):not(.period_question) { grid-column: 1 / -1; }
    .review_row .period_question { justify-self: end; justify-items: end; }
    footer { flex-wrap: wrap; }
  }
  @media (max-width: 520px) {
    .vote_row { grid-template-columns: 1fr; }
    .topbar { gap: var(--control-gap); padding: 0 1rem; }
    .survey .page { padding: 2rem .75rem 3rem; }
    .survey h1 { font-size: var(--s-fs-title); }
    .meta > * { min-width: 0; max-width: 100%; }
  }
</style>
